import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addMessage, addEvent, removeOptions, setWaiting, setWidget, clearEvents, markEventsStale, setAssistantStatus, setTransientNotice, setFloatingNotice, setRoomAliases, setActiveLive } from './store/chatSlice';
import { LivePanel, LiveTabs, LiveLink, liveLinkIn } from './LivePanel';
import FloatingNotice from './FloatingNotice';
import { aliasesFrom, shownName } from './roomAliases';
import { connectWebSocket, disconnectWebSocket, sendText, sendLoadOlder, sendLiveOpen, sendTyping, sendSplit, sendSendToDisplay, sendGlobalAction } from './store/Actions';
import { ensureSubscribed, pushPermission, isPushOptInDismissed, dismissPushOptIn } from './webpush';
import PushOptIn from './PushOptIn';
import { wsUrl } from './store/wsUrl';
import { currentCoords } from './currentLocation';
import { reconnectWs } from './store/reconnectWs';
import { getOutputToken, isEmbed, getEmbedId, isReinvokeEmbed, getFallbackStation, getStationNetwork, getStationUrlPath, setEmbedStation, takePendingSay } from './store/websocketMiddleware';
import TypingEffect from './TypingEffect';
import { newestPanelCard, parsePanelCard, CARD_TO_CHAT } from './panelFocus';
import InputWidget from './widgets/InputWidget';
import AuthHeader, { RAIL_WIDTH } from './AuthHeader';
import StationPic from './StationPic';
import DisconnectedOverlay from './DisconnectedOverlay';
import AnonLoginPrompt from './AnonLoginPrompt';
import LoginModal from './LoginModal';
import CommandAutocomplete from './CommandAutocomplete';
import { getViewer, FILL_VIEWERS } from './viewers/ViewerRegistry';
import CardViewer from './viewers/CardViewer';
import { EMBED_HEIGHT_MSG, EMBED_LASTRUN_MSG } from './cards/embedHeight';
import { embedCardNeedsChannel } from './cards';
import { cardIndexEntry } from './cards/sandbox/cardIndex';
import { recordStationVisit } from './recentStations';
import useIsMobile from './hooks/useIsMobile';
import useBodyScrollLock from './hooks/useBodyScrollLock';
import useReconnectOnReturn from './hooks/useReconnectOnReturn';
import { relativeTime, absoluteTime } from './timeFormat';
import PairDisplayModal from './PairDisplayModal';
import SendGlyph from './sendGlyph';
import StationSettingsModal, { hasStationSettings } from './StationSettingsModal';
import { canPair } from './pairDisplay';
import { buildStationNavLinks } from './stationNav';
// station-chat-files slug: paste/drag-drop upload + the `@aveq upload/download
// file` commands. Uploading posts an ORDINARY room message carrying a markdown
// link, so nothing about the message wire, the helper or the renderer changes.
import { parseFileCommand, filesFromDataTransfer, uploadFile, listFiles, humanSize, imageFailure, shareableImage } from './chatFiles';
import ChatMessageBody from './ChatMessageBody';
import { PreviewCard, useLinkPreview } from './LinkPreview';
import { useSharedResult, sharedResultFrom, stationOutputActions, asOfLabel } from './sharedResult';
import GalleryBookmarkButton from './GalleryBookmarkButton';
import { shareLinkIn } from './chatUnfurl';
// voice-input: speak a message instead of typing it. Safari's OWN dictation can
// only fill the box — it looks like typing to the page, so there is no
// end-of-utterance signal to submit on. The Web Speech API gives us one.
import { isSupported as voiceSupported, startSession as startVoice,
         withMention, describeError as describeVoiceError,
         SEND_DELAY_MS } from './voiceInput';
// voice-input: the cancel window a finished transcript waits in. A module, not
// four refs here, because the interaction it gets right — a second utterance
// landing while the first is still waiting — is only reachable through timing
// and needs to be a test rather than a thing you reproduce with a microphone.
import { createCancelWindow } from './voiceQueue';

// voice-input: how many CONSECUTIVE recognition failures end voice mode. A
// refused microphone is handled separately (`blockedRef`) because it can never
// succeed on retry; this is for the transient kind — network, no device — where
// one more try is reasonable but an unbounded loop the user cannot see is not.
const MAX_VOICE_FAILURES = 3;
// Safari needs a beat to release the microphone before it can be re-acquired;
// restarting immediately fails with `already-started`.
const VOICE_RESTART_MS = 400;

// chat-helper-completion slug (#3): compose the "is typing…" line from the set
// of currently-typing peers. Names beyond two collapse to a generic phrase so
// the line never grows unbounded.

/**
 * The mobile header's pinning, as a value rather than an inline literal — so
 * the three properties that have to agree can be asserted instead of eyeballed.
 *
 * They are one mechanism, not three choices:
 *   position/top  pins the bar to the viewport as the page scrolls, which is
 *                 what lets iOS collapse its toolbar;
 *   background    makes it opaque, so messages pass UNDER rather than through;
 *   zIndex        puts it above them;
 *   borderBottom  draws the boundary the chat card can no longer draw itself.
 *
 * That last one is the fix for the reported bug and the least obvious: the
 * card's own rounded top border slides beneath the bar the instant you scroll,
 * so without a rule the chat reads as an unbounded white area running into a
 * page-coloured band — the border looks clipped rather than covered. Drop any
 * one of these while tidying and the symptom returns silently, which is why
 * they are pinned together in a test.
 *
 * Desktop keeps the header in normal flow; nothing to pin, nothing to draw.
 */
/**
 * The gap above and below the chat card on mobile, as ONE value.
 *
 * They are required to be equal — the card should sit the same distance from
 * the header above it as from the input box below it — so this is a single
 * constant rather than two literals that happen to agree today. Two literals is
 * how they stop agreeing.
 */
export const CHAT_GAP = '6px';

/* chat-panel-width: the desktop chat column's maximum width.
 *
 * 820 is not a new number — it is the cap the output drawer already used, and
 * drawerStyle now reads it FROM HERE (`min(CHAT_MAX_WIDTH, 66vw)`) rather than
 * repeating the literal. The two surfaces share this row, so they share the
 * symbol; a comment asserting they agree is worth nothing if either can be
 * edited alone.
 *
 * It is a CAP, not a width: the panel takes what the row can give up to this,
 * and the surrounding space absorbs the remainder. See leftPanelStyle for why
 * that ordering is the point.
 */
export const CHAT_MAX_WIDTH = 820;

/* output-idle-logo: the idle watermark's two decisions, exported so the merge
 * gate can see them — this file is only reachable from tests through its
 * exports (see ChatInterface.headerSeam.test.js for the same pattern).
 *
 * The mark stands in for content that isn't there yet, mirroring what the Presto
 * shows on an empty screen (presto-idle-logo, aveq-agent/bff/view_image.py).
 * Two values are shared with it deliberately: 92% of the SHORTER dimension, and
 * grayscale — the Presto desaturates via `logo.convert("L")` so the whisper
 * reads as a plain gray tint rather than pale pink, and `filter: grayscale(1)`
 * is the CSS equivalent.
 *
 * Opacity deliberately does NOT match: 0.04 here against the Presto's 0.08.
 * That panel is small, reflective and viewed across a room; this is a bright
 * backlit screen a foot from the reader, where 0.08 read as too present.
 */
export const IDLE_WATERMARK_OPACITY = 0.04;

export const idleWatermarkImgStyle = {
  maxWidth: '92%',
  maxHeight: '92%',
  opacity: IDLE_WATERMARK_OPACITY,
  filter: 'grayscale(1)',
};

// Only when there is genuinely nothing to show. The viewers render nothing for
// an empty `events` (CardViewer leaves eventData null), which is the blank
// rectangle this fills; once a card lands the mark must get out of the way
// rather than sit behind content.
export const showIdleWatermark = (events) => !events || events.length === 0;

// A station without live output (a room) still has the drawer, for what is opened from chat.
const NO_EVENTS = [];

/** station-anon-login-prompt: should this page show "sign in" instead of a spinner?
 *
 * What an anonymous visitor on a plain station page actually gets (verified in the
 * browser, not inferred): the socket connects, SUBSCRIBER_METRICS ARRIVES with the
 * full station record, `connectionStatus` goes 'live', and `setWaiting(true)` starts
 * the three-dot typing indicator. Then nothing. The thread is created in the MAIL
 * handler -- which is also the only place `setWaiting(false)` and `setThreadId` run --
 * and no MAIL ever comes, because there is no account to own a thread. So the dots
 * spin forever on a connection that is, as far as the client is concerned, healthy.
 *
 * Three conditions, each load-bearing:
 *  - `!outputOnly` -- embeds (`/embed/s/<id>`, config.embed_public / embed_act) and
 *    second-screen observers with an output token are MEANT to work anonymously.
 *    Prompting there would break them.
 *  - `anonAuthed === false` -- strictly false, not falsy. `null` means /auth/me has
 *    not answered yet, and treating that as signed-out flashes a login wall at every
 *    signed-in visitor on first paint.
 *  - `!threadId` -- the room is demonstrably unusable. Deliberately NOT
 *    `connectionStatus`: the connection reports 'live' here, so any check on it is
 *    false. Keyed on the thread so that a station which DOES hand an anonymous
 *    visitor a thread keeps working untouched -- the prompt appears only where chat
 *    genuinely cannot start.
 */
export const shouldShowAnonLoginPrompt = ({ outputOnly, anonAuthed, threadId }) =>
  !outputOnly && anonAuthed === false && !threadId;

/** billing-pdf-ingest: may this station accept a pasted / dropped file?
    A shared room always (station-chat-files); a private station only when its
    config opts in with `attachments: true` — there the posted link goes to the
    LLM, and the agent expands it into the file's text (aveq-agent/attachments.py),
    which is only useful on a station whose helper knows what to do with a
    document. Every other private station stays exactly as it was. */
export const canShareFilesOn = (cfg, isRoom) => !!isRoom || !!(cfg && cfg.attachments);

/** attachments-multi-file: has the assistant REPLIED to the file message posted when the
    chat log had `before` entries? The post itself adds the local echo (index `before`), so
    the reply is a later, non-local message. This — not the `waiting` flag — is the "turn
    finished" signal: `waiting` also flips false on an ephemeral status beat ("checking…"),
    which would release the next file mid-turn and reproduce the bug this guards. */
export const turnRepliedSince = (messages, before) => {
  const m = messages || [];
  return m.length >= before + 2 && !m[m.length - 1].isLocal;
};

/** chat-attach-with-message: is the turn whose echo landed at log index `before` still
    awaiting its reply? A log shorter than that was cleared (`/clear`), so nothing is pending. */
export const turnInFlight = (messages, before) => {
  const m = messages || [];
  return before !== null && before !== undefined && m.length > before && !turnRepliedSince(m, before);
};

/** chat-attach-with-message: the one message Send posts for typed text plus a staged file.
    A file posted as its own message is a second turn — a second search, or, when it lands
    mid-turn, a continuation whose reply is lost. */
export const composeWithAttachment = (text, markdown) => {
  const t = (text || '').trim();
  return t ? `${t}\n\n${markdown}` : markdown;
};

// The embed's "Open <station>" link, or null. Rechecks the exact `/s/<url_token>` shape the
// BFF sends, so nothing else (another origin, a javascript: URL) can become a link.
export function safeStationLink(link) {
  if (!link || typeof link !== 'object') return null;
  const url = typeof link.url === 'string' ? link.url : '';
  const name = typeof link.name === 'string' ? link.name.trim() : '';
  if (!/^\/s\/[A-Za-z0-9_-]{8,64}$/.test(url) || /^\/s\/\d+$/.test(url) || !name) return null;
  return { url, name: name.slice(0, 80) };
}

export function stickyHeaderStyle(isMobile) {
  if (!isMobile) return {};
  return {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    background: '#fafbfc',
    borderBottom: '1px solid #e0e0e0',
    // Breathing room INSIDE the bar, so the rule is an edge rather than an
    // underline on the station name. Not part of the gap — the gap is measured
    // from the rule down to the card, and that is CHAT_GAP on the panel below.
    paddingBottom: CHAT_GAP,
  };
}

const typingText = (names) => {
  if (!names || names.length === 0) return '';
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return 'Several people are typing…';
};

// voice-icon-minimal: the composer's voice toggle, as line icons. Stroke-only,
// currentColor, 1.8px on a 24 grid at 22px — the weight of the send arrow
// beside them, not a colour emoji's.
export function MicIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         data-icon="mic">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

export function KeyboardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         data-icon="keyboard">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10" />
    </svg>
  );
}

// scheduled-result-view: the in-conversation notice that a background job
// finished. A card-shaped affordance rather than a sentence with a link in it,
// because it is not something the assistant said — it is a pointer to a result
// held elsewhere, and it should not read as part of the thread's prose.
//
// An <a href>, not a click handler: the target must survive a reload and a
// paste into a message. onNavigate intercepts so the caller can run the
// leave-station confirmation first — following it unloads the station page.
// Exported for ChatInterface.scheduledRunLink.test.js. ChatInterface itself is
// not mountable in a test (redux + WS + station fetch), so this file's
// convention — see ChatInterface.idleLogo.test.js — is that anything worth
// asserting lives in an export. !686's review: the middleware dispatch and the
// reducer were both covered and the RENDER SITE was not, which is the one link
// that already shipped broken once.
//
// run-result-unfurl: when the run has a snapshot (`run.embedUrl`, pre-minted by
// `notification_watcher`), this draws the SAME preview card a pasted share link
// gets, and clicking it opens the result in the output panel — the conversation
// stays on screen. The pill above is what it replaces, and what it falls back to
// whenever the preview does not resolve: a failed run, a run with no card, a
// record tidied away, an offline moment. The fallback is deliberate — the pill
// is the only affordance those runs have, and a chat that silently shows nothing
// when a job finishes is worse than one that shows a plain link.
//
// The swap happens on resolve rather than up front, for `ChatMessageBody`'s
// reason: a card that never arrives must not leave a hole where the pointer was.
export function ScheduledRunLink({ run, taskName, label, onNavigate,
                                   onOpenShared, openHint, compact = false }) {
  const failed = run.status === 'err';
  const cards = run.cardCount || 0;
  // A no-op for a run with no snapshot: `useLinkPreview` finds no share link in
  // an empty string and never fetches. Called unconditionally because it is a
  // hook.
  const { preview } = useLinkPreview(run.embedUrl || '');
  // The task name is the header AND, for a task with no separate description,
  // the card's own title. Showing it twice is noise, so the header stands down
  // when the card is already saying it.
  const heading = taskName || 'Scheduled task';
  const headingDuplicated = preview
    && typeof preview.title === 'string'
    && preview.title.trim() === heading.trim();
  if (preview) {
    return (
      <div style={{ maxWidth: '80%' }}>
        {!headingDuplicated && (
          <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 2, fontWeight: 600 }}>
            {heading}
          </div>
        )}
        <PreviewCard preview={preview} onNavigate={onNavigate} compact={compact}
                     onOpenShared={onOpenShared} openHint={openHint} />
      </div>
    );
  }
  return (
    <div style={{ maxWidth: '80%' }}>
      <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 2, fontWeight: 600 }}>
        {heading}
      </div>
      <a
        href={run.href}
        onClick={(e) => {
          // Let a modified click (new tab / window) through untouched — the
          // page is not being unloaded, so the confirmation would be noise.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          onNavigate(run.href);
        }}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', borderRadius: '12px 12px 12px 2px',
          background: failed ? '#fef2f2' : '#f1f5f9',
          border: `1px solid ${failed ? '#fecaca' : '#e2e8f0'}`,
          color: failed ? '#b91c1c' : '#0f172a',
          fontSize: '0.9rem', textDecoration: 'none',
        }}
      >
        <span aria-hidden="true"
          style={{ width: 7, height: 7, borderRadius: '50%', flex: '0 0 auto',
                   background: failed ? '#ef4444' : '#22c55e' }} />
        <span>{label || (failed ? 'Failed' : 'Finished')}</span>
        {/* Only when there is something to count. "1 card" on every single-card
            run is noise; the absence of a count means "nothing to preview". */}
        {!failed && cards > 0 && (
          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
            · {cards} card{cards === 1 ? '' : 's'}
          </span>
        )}
        <span style={{ color: '#2563eb', fontWeight: 600 }}>View result ↗</span>
      </a>
    </div>
  );
}

const ChatInterface = () => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { messages, user, connectionStatus, station, threadId, events, eventsRestored, live, viewer, waiting, prompting, widget, typingPeers, transientNotice, floatingNotice, assistantStatus, splitToken, observeStatus, roomAliases } = useSelector(state => state.chat);
  // output-second-screen slug: a share token in the URL (/s/<id>/<token>) puts
  // this tab in read-only "output-only" mode — it observes the owner's thread
  // and renders only the viewer (card) panel. See the early return below.
  // embeddable-card: /embed/s/<id> renders output-only (no chat chrome) — the same
  // gate as a /view second screen, so isEmbed() folds into outputOnly and reuses the
  // whole observe/no-unload-warn/stale-refresh path.
  const outputOnly = getOutputToken() || isEmbed();

  // output-second-screen slug: does this station surface a card OUTPUT panel at
  // all? `output_observers_max === 0` means "no output panel" (e.g. a chat-only
  // StationChat room); >= 1 (default 1) means the panel exists plus that many
  // shareable second screens. Missing/malformed → default to showing it.
  const _outputCapRaw = station?.station?.config?.output_observers_max;
  const outputCap = (_outputCapRaw == null || _outputCapRaw === '') ? 1 : Number(_outputCapRaw);
  const hasOutputPanel = !Number.isFinite(outputCap) || outputCap >= 1;

  // save-this-button: the card on screen can be saved to the Gallery when its package
  // declares `save_as` — the read-only action that reproduces it. Gating the control on the
  // DECLARATION (rather than showing it always) means the button never offers a save that the
  // fail-closed embeddable gate would then refuse. Cards without it keep the chat path.
  const [savableCard, setSavableCard] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    const card = newestPanelCard(events);
    const kind = card && card.kind;
    if (!kind) { setSavableCard(null); return undefined; }
    cardIndexEntry(kind)
      .then((entry) => {
        if (cancelled) return;
        const sa = entry && entry.save_as;
        // `note` is the author saying what saving THIS card does — a saved tile can be a
        // live demo that re-runs on new data rather than a snapshot of what is on screen.
        // It reaches the user through the button's `title` and `aria-label` only. It was
        // ALSO rendered inline beside the button, which is the surface that does not need a
        // hover, but the sentence crowded the panel chrome and ellipsised; removed on
        // request. The trade-off is real and worth knowing: a touch user gets no disclosure
        // before clicking. If that matters, the fix is a SHORT always-visible marker (a
        // "live" chip), not the full sentence back.
        setSavableCard(sa ? { kind, note: sa.note || '' } : null);
      })
      .catch(() => { if (!cancelled) setSavableCard(null); });
    return () => { cancelled = true; };
  }, [events]);
  // output-panel-chrome: "Split output" (mirror this thread onto a second screen)
  // only when the station allows at least 2 output panels (output_observers_max >= 2).
  // Malformed/unbounded cap → permissive (show).
  const canSplitOutput = !Number.isFinite(outputCap) || outputCap >= 2;
  // output-panel-chrome: "Pin to display" only when a display is actually linked to
  // this station. The BFF stamps `has_paired_display` onto the station in the
  // SUBSCRIBER_METRICS config overlay (a display whose config.show_station points
  // here). Absent/false → hide (fail-closed).
  const hasPairedDisplay = station?.station?.has_paired_display === true;
  // nav-mobile-menu: the station header's "Network" nav item → that station's
  // network directory page. Prefer the network the BFF stamps from the station's
  // node; the page stamp covers the first render before metrics arrive.
  const _stationNetwork = station?.station?.network || getStationNetwork() || null;
  // Built in `stationNav.js` so the list is unit-testable without mounting the chat app.
  const stationNavLinks = buildStationNavLinks(_stationNetwork);
  // station-url-hash: this station's /s/<url_token> chat address (hub-stamped on the spec).
  const _stationUrlPath = station?.station?.url_token
    ? `/s/${station.station.url_token}` : getStationUrlPath();
  // chat-helper-completion slug (#3): typing presence only makes sense in a
  // shared room (a multi-visitor station). Gate both sending our own typing
  // pings and showing others' on this so 1-1 assistant stations stay quiet.
  // broadcasting-as-shared-session (D2): the room signal is now the decomposed
  // `cardinality: 'shared'` axis; the legacy `resolver_mode === 'broadcasting'`
  // is honored as a fallback for not-yet-migrated stations during the transition.
  const _cfg = station?.station?.config || {};
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const isRoom = _cfg.cardinality === 'shared' || _cfg.resolver_mode === 'broadcasting';
  // chat-helper-polish slug: per-message timestamps show in station-chat rooms
  // (the broadcasting / station_chat_endpoints helper) and nowhere else — on a
  // 1-1 assistant station they're just noise. Tied to the room signal, not an
  // operator toggle, so it's automatic: chat rooms get timestamps, everything
  // else doesn't.
  const showTimestamps = isRoom;
  // billing-pdf-ingest: rooms, plus private stations that opt in (see canShareFilesOn).
  const canShareFiles = canShareFilesOn(_cfg, isRoom);
  // Throttle outbound typing pings to at most one per 3s while the user types.
  const lastTypingSentRef = useRef(0);
  // Derived: only "live" enables send. 'connecting' = initial open or
  // post-reconnect wait, 'stale' = disconnected awaiting user tap.
  const isConnected = connectionStatus === 'live';
  const isStale = connectionStatus === 'stale';
  // station-settings-mobile-sheet: no settings while the reconnect prompt is up; an
  // open dialog is discarded when the connection drops.
  const openStationSettings = hasStationSettings(station?.station) && !isStale
    ? () => setSettingsOpen(true) : null;
  useEffect(() => { if (isStale) setSettingsOpen(false); }, [isStale]);
  // Fetch the position before the first message, so that one can carry it too.
  useEffect(() => { currentCoords(); }, []);
  // The viewer's aliases for people in this room, applied wherever a sender is shown.
  const settingsStationId = hasStationSettings(station?.station) ? station?.station?.station_id : null;
  useEffect(() => {
    dispatch(setRoomAliases(null));
    if (!settingsStationId) return undefined;
    let cancelled = false;
    fetch(`/api/stations/${encodeURIComponent(settingsStationId)}/my-settings`, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => { if (!cancelled && body) dispatch(setRoomAliases(aliasesFrom(body))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [settingsStationId, dispatch]);

  // station-anon-login-prompt: an anonymous visitor on a PLAIN station page connects
  // fine (bff/ws.py accepts account_id=None) and even receives SUBSCRIBER_METRICS --
  // then waits forever for a MAIL that cannot come, leaving the typing dots spinning
  // on a 'live' connection. `null` = not yet known, so nothing flashes before
  // /auth/me answers.
  //
  // Deliberately NOT applied to the anonymous paths that are MEANT to work without an
  // account: `/embed/s/<id>` embeds (config.embed_public / embed_act) and second-screen
  // observers carrying an output token. Those legitimately have no session, and
  // prompting there would break working embeds.
  const [anonAuthed, setAnonAuthed] = React.useState(null);
  const [loginOpen, setLoginOpen] = React.useState(false);

  React.useEffect(() => {
    // Reuses the EXISTING `outputOnly` derivation (getOutputToken() || isEmbed())
    // rather than restating it -- two expressions for one concept drift apart.
    if (outputOnly) { setAnonAuthed(true); return undefined; }
    let alive = true;
    (async () => {
      try {
        const r = await fetch('/auth/me', { credentials: 'include' });
        // !769 review #2: ONLY 401 means signed out. `r.ok` treated every non-2xx as
        // anonymous, and /auth/me has others: `_me` raises 404 "profile missing" for an
        // AUTHENTICATED account with no profile row (bff/auth.py), and a 502/503 while the
        // agent restarts is not a logged-out user either. Both would have put a login wall
        // in front of a signed-in visitor -- the same failure the catch below exists to
        // prevent, which until now only covered network errors.
        if (alive) setAnonAuthed(r.status !== 401);
      } catch (_) {
        // A failed probe must not masquerade as "signed out" -- that would put a
        // login wall in front of a signed-in user whose network blipped. Treat
        // unknown as authed and let the normal connect path report the truth.
        if (alive) setAnonAuthed(true);
      }
    })();
    return () => { alive = false; };
    // !769 review #4: re-probe per SESSION, not once per mount. Logging out via
    // AuthHeader bounces the socket but leaves this effect unrun, so `anonAuthed`
    // stayed true and the visitor was back to spinning dots with no prompt. Both
    // login and logout produce a new sessionId, so keying on it covers both without
    // coupling to AuthHeader.
  }, [outputOnly, station?.sessionId]);

  // Only once we KNOW they are anonymous, and only while the room has not come up.
  const showAnonPrompt = shouldShowAnonLoginPrompt({ outputOnly, anonAuthed, threadId });
  const [inputMessage, setInputMessage] = React.useState('');
  const commandAcRef = React.useRef(null);   // command-autocomplete: textarea delegates ↑/↓/Enter/Esc here
  const [inputFocused, setInputFocused] = React.useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  // station-chat-load-older: tracks the oldest (first) message's id so the
  // scroll effect can tell a "load earlier" prepend (first id drops) from a
  // normal append, and scroll to the top of the loaded history instead of
  // jumping back to the bottom.
  const prevFirstIdRef = useRef(null);
  const inputFormRef = useRef(null);
  // mobile-keyboard-dismiss: ref to the chat textarea + a handler that blurs it
  // on a touch anywhere OUTSIDE the input form, so iOS Safari retracts the
  // keyboard (it stays up until the focused control loses focus). Touches inside
  // the form (textarea, send button, command autocomplete) are left alone;
  // sandboxed card iframes are opaque OOPIFs whose touches never bubble up here.
  const inputRef = useRef(null);
  // unload-conversation-guard: set true right before an intentional in-app leave
  // (the "Leave station" confirm navigates via window.location.href) so the
  // beforeunload guard below doesn't stack a native prompt on our own dialog.
  const dismissKeyboardOnOutsideTouch = useCallback((e) => {
    if (!inputFormRef.current?.contains(e.target)) inputRef.current?.blur();
  }, []);
  // mobile-keyboard-gap: iOS OVERLAYS the virtual keyboard without resizing the
  // layout, and when it closes it can leave the document scrolled up — so the
  // sticky input stays lifted with a blank gap below it instead of dropping to
  // the screen bottom. On blur (any dismiss path), wait for the visual viewport
  // to settle after the keyboard animates away, then snap the page to the bottom
  // so the input returns to the edge. Fires once; 400ms fallback if no resize.
  const snapInputToBottomAfterKeyboard = useCallback(() => {
    const toBottom = () => window.scrollTo(0, document.documentElement.scrollHeight);
    const vv = window.visualViewport;
    if (!vv) { setTimeout(toBottom, 300); return; }
    let done = false;
    const settle = () => {
      if (done) return;
      done = true;
      vv.removeEventListener('resize', settle);
      toBottom();
    };
    vv.addEventListener('resize', settle);
    setTimeout(settle, 400);
  }, []);
  // Form is floated over the messages list (translucent backdrop). Track
  // its height so we can pad the scroll container by exactly that amount
  // — otherwise the textarea growing past the static padding hides the
  // last message under the input.
  const [formHeight, setFormHeight] = React.useState(72);
  // The output (card) panel is a right-side drawer over the chat. New card output and the chat's
  // result links open it; clicking the chat slides it shut. Stations with output_observers_max === 0
  // (rooms) have no live output; their drawer shows only what is opened from the chat.
  const [outputOpen, setOutputOpen] = React.useState(false);

  // share-link-unfurl: a shared result opened from a chat link, shown in the
  // output panel INSTEAD of the live stream. `{title, cards}` or null.
  //
  // Local state, never redux: the live `events` stream is the station's, shared
  // with the split second screen and the Presto, and a link someone pasted in
  // chat must not redirect what a wall display is showing. This is one reader
  // looking at one thing; the live output is never disturbed, so standing the
  // snapshot down puts it straight back.
  //
  // WHEN it stands down lives in `useSharedResult`: the next live output event.
  // run-result-unfurl removed the button that used to do it, and without a
  // replacement the panel froze on the opened result and every later answer
  // landed behind it (!747 review).
  const liveEvents = hasOutputPanel ? events : NO_EVENTS;
  const [sharedInOutput, setSharedInOutput] = useSharedResult(liveEvents);
  const outputActions = stationOutputActions({
    embedPublic: hasOutputPanel && _cfg.embed_public, savable: hasOutputPanel && savableCard,
    shared: sharedInOutput });

  // Fetch the shared card and show it, via the endpoint the shared page uses. A
  // snapshot reads its copied cards; a live card (`preview.live`) runs now, on
  // this click, as opening its link would.
  const openSharedInOutput = React.useCallback(async (preview) => {
    dispatch(setActiveLive(null));
    setSharedInOutput({ ...sharedResultFrom(preview, null), loading: true });
    setOutputOpen(true);
    try {
      const res = await fetch(`/api/embed/s/${encodeURIComponent(preview.id)}`);
      setSharedInOutput(sharedResultFrom(preview, res.ok ? await res.json() : null));
    } catch (e) {
      setSharedInOutput(sharedResultFrom(preview, null));
    }
  }, [dispatch]);

  // autoroute-continuity-pin: re-run a session card's stored call; the agent decides whether
  // it may.
  const refreshSharedInOutput = React.useCallback(async (id) => {
    setSharedInOutput((cur) => (cur && cur.id === id ? { ...cur, refreshing: true } : cur));
    let body = null;
    try {
      const res = await fetch(`/api/embed/s/${encodeURIComponent(id)}/refresh`,
        { method: 'POST', credentials: 'include' });
      body = res.ok ? await res.json() : null;
    } catch (e) { /* keep the stored card */ }
    setSharedInOutput((cur) => {
      if (!cur || cur.id !== id) return cur;
      if (!body || !Array.isArray(body.cards)) return { ...cur, refreshing: false };
      return { ...cur, cards: body.cards, asOf: body.as_of || cur.asOf, refreshing: false };
    });
  }, []);      // eslint-disable-line react-hooks/exhaustive-deps

  const openShared = openSharedInOutput;

  // On a phone, Download opens the share sheet (Save Image) instead: iOS shows a saved
  // file in place of the app, with no way back from a home-screen install.
  const shareImageRef = React.useRef({ url: null, file: null });
  const panelImageUrl = sharedInOutput && sharedInOutput.image ? sharedInOutput.image.url : null;
  const panelImageName = sharedInOutput && sharedInOutput.image ? sharedInOutput.image.name : null;
  React.useEffect(() => {
    shareImageRef.current = { url: panelImageUrl, file: null };
    if (!panelImageUrl) return;
    shareableImage(panelImageUrl, panelImageName).then((file) => {
      if (shareImageRef.current.url === panelImageUrl) shareImageRef.current.file = file;
    });
  }, [panelImageUrl, panelImageName]);
  const onImageDownload = (e) => {
    const { file } = shareImageRef.current;
    if (!file) return;
    e.preventDefault();
    navigator.share({ files: [file] }).catch(() => {});
  };

  // A local link opens the page's own copy of the cards, as they were.
  const openLocalInOutput = React.useCallback((msg) => {
    dispatch(setActiveLive(null));
    const first = msg.localCards.map(parsePanelCard).find((c) => c && c.title);
    setSharedInOutput({ id: null, title: first ? first.title : 'Result', cards: msg.localCards,
                        bookmarked: null, loading: false,
                        asOf: new Date(msg.timestamp).toISOString(), refreshable: false });
    setOutputOpen(true);
  }, [dispatch]);      // eslint-disable-line react-hooks/exhaustive-deps

  // A posted image opens full-size in the panel, which carries its Download.
  const openImageInOutput = React.useCallback(({ url, name }) => {
    setSharedInOutput({ id: null, title: name, image: { url, name }, cards: [],
                        bookmarked: null, loading: false, asOf: null, refreshable: false });
    setOutputOpen(true);
  }, []);      // eslint-disable-line react-hooks/exhaustive-deps


  // presto-qr-pairing: the "Pair ⧉" dialog (photograph or type the screen's code).
  const [pairOpen, setPairOpen] = React.useState(false);
  const [stationInfoOpen, setStationInfoOpen] = React.useState(false);
  // station-chat-files slug: upload/download state. `uploading` is a count so
  // several pasted files at once show one honest "Uploading 3 files…" line;
  // `fileList` is the transient panel `@aveq download file` opens (never a
  // message — a listing is personal, not something to broadcast to the room).
  // voice-input state. `pendingVoice` holds a finished transcript during its
  // cancel window — these commands drive hardware ("screen off" vs "screen on"
  // differ by one phoneme), so a misrecognition gets a beat to be stopped.
  // voice-input: voice MODE — an alternative to typing rather than a one-shot.
  // While on, the text box is replaced by the live transcript and recognition
  // restarts after each utterance, so you can speak several commands without
  // touching the keyboard. Remembered per-browser, because a user who works this
  // way wants it on again next time (localStorage is the documented fallback for
  // per-viewer conveniences; it is read defensively — a private window or
  // blocked site-data throws on access).
  const [voiceMode, setVoiceMode] = React.useState(() => {
    try { return window.localStorage.getItem('aveq.voiceMode') === '1'; }
    catch (e) { return false; }
  });
  // `onend` fires from inside a recognition session and must see the CURRENT
  // mode to decide whether to restart — a state value would be stale there.
  const voiceModeRef = useRef(voiceMode);
  const restartRef = useRef(null);
  const failuresRef = useRef(0);
  // Separate from the failure streak on purpose: a refused microphone can never
  // succeed on retry, so it is a different thing from "three bad attempts" and
  // must survive a threshold change to the streak.
  const blockedRef = useRef(false);
  // voice-input: a brief "pressed" flash on the Send button whenever a message
  // goes out — clicked, Enter, or spoken. In voice mode nothing else moves at the
  // moment of sending (there is no box to empty), so without this the only
  // feedback is the message appearing; the flash makes the send itself visible,
  // and keeps both modes behaving the same way.
  const [sendFlash, setSendFlash] = React.useState(false);
  const sendFlashRef = useRef(null);
  const [listening, setListening] = React.useState(false);
  const [heard, setHeard] = React.useState('');          // live interim text
  const [pendingVoice, setPendingVoice] = React.useState(null);
  const [queuedCount, setQueuedCount] = React.useState(0);
  const voiceRef = useRef(null);
  const [uploading, setUploading] = React.useState(0);
  const [dragActive, setDragActive] = React.useState(false);
  const [fileList, setFileList] = React.useState(null);
  const fileInputRef = useRef(null);
  // chat-helper-completion slug (#2): a single shared clock for all relative
  // message timestamps. Ticks once a minute so "just now" → "1m ago" ages on
  // its own; one interval re-renders every label in step (cheaper than a
  // per-message timer). Absolute time is always available on hover.
  const [nowTick, setNowTick] = React.useState(Date.now());
  useEffect(() => {
    // chat-helper-polish: only tick when this station shows timestamps —
    // otherwise (the default) there's no relative label to age, so skip the
    // once-a-minute full-list re-render entirely.
    if (!showTimestamps) return;
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, [showTimestamps]);

  // dashboard-revamp: log this visit for /dashboard's "recently accessed"
  // section. Fires when the station identity lands (i.e. a real connected
  // chat); skipped for read-only output-observer windows — a second screen
  // is not a visit. The pathname is recorded post-redirect, so it's the
  // canonical /s/<network>/<id> or /<network>/<slug> address.
  useEffect(() => {
    if (outputOnly) return;
    const st = station?.station;
    if (st?.station_id && st?.name) {
      recordStationVisit({
        stationId: st.station_id,
        name: st.name,
        path: window.location.pathname,
        image: st.config?.image_url,
      });
    }
  }, [outputOnly, station]);


  useEffect(() => {
    if (!inputFormRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setFormHeight(Math.ceil(e.contentRect.height));
      }
    });
    ro.observe(inputFormRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // Scroll the messages container all the way down. We can't use
    // scrollIntoView on messagesEndRef here: the input form floats over
    // the bottom of this container (`position: absolute`), so the
    // viewport's *visible* bottom is occluded — block:"end" would pin
    // the ref behind the form, and default block:"start" would push the
    // latest message above the viewport. Instead we scroll to max,
    // which lands the ref inside the dynamic `paddingBottom` band; the
    // padding reserves enough empty space (formHeight + 24) so the
    // last *real* message sits just above the form's top edge.
    // station-chat-load-older: if the OLDEST (first) message's id just dropped,
    // a "load earlier" page was prepended — scroll to the top so the user
    // lands on the earliest loaded message instead of being yanked back to the
    // bottom. Any other change (live/join/append) scrolls to the bottom.
    const firstId = messages.length ? Number(messages[0].msgId) : null;
    const prevFirstId = prevFirstIdRef.current;
    prevFirstIdRef.current = firstId;
    const prepended = (
      Number.isFinite(firstId) && Number.isFinite(prevFirstId) && firstId < prevFirstId
    );
    // Mobile scrolls the DOCUMENT (window); desktop scrolls the inner container.
    if (isMobile) {
      const top = prepended ? 0 : document.documentElement.scrollHeight;
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      const c = messagesContainerRef.current;
      if (!c) return;
      c.scrollTo({ top: prepended ? 0 : c.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isMobile]);

  // embeddable-card-live-action: re-invoke the embed's stored action and REPLACE
  // the rendered card(s). Used on open AND by the header refresh button. Tracks
  // the last successful run time for the faint footer stamp. A failed refresh
  // keeps whatever's on screen (never blanks a working card on a transient error).
  const [embedLastRun, setEmbedLastRun] = useState(null);
  const [embedRefreshing, setEmbedRefreshing] = useState(false);
  const [embedError, setEmbedError] = useState(false);
  // card-gallery: whether this embed is PUBLIC (shared / station-public) — the
  // onaveq branding header shows only then, so an owner's private gallery tile
  // carries no logo. Server-supplied (`public` on the /api/embed response).
  const [embedPublic, setEmbedPublic] = useState(false);
  const [embedStationLink, setEmbedStationLink] = useState(null);
  const [embedBookmarked, setEmbedBookmarked] = useState(null);
  const embedAbortRef = useRef(null);
  const embedGenRef = useRef(0);   // increments per refresh → forces card re-mount
  const embedWsRef = useRef(false); // reinvoke-embed-actions: action channel opened (once)
  const embedRootRef = useRef(null);   // gallery-fit-content: the content-fit embed root, for the height beacon
  // gallery-card-paging: has this MOUNT already told the paired screen to start at page 1?
  // One reset per mount is the whole contract — see the call site.
  const screenResetRef = useRef(false);

  const refreshEmbed = useCallback(async (force = false, resume = '') => {
    // /ultrareview #6: abort any in-flight fetch so a slow prior request can't
    // clobber a newer one (double-click / unmount race) or setState after unmount.
    if (embedAbortRef.current) embedAbortRef.current.abort();
    const ac = new AbortController();
    embedAbortRef.current = ac;
    setEmbedRefreshing(true);
    try {
      // Manual refresh (button) sends ?fresh=1 to bypass the server display TTL;
      // the auto-open fetch omits it (honors the TTL rate-limit). `cache:
      // 'no-store'` stops the browser serving its own HTTP-cached copy without
      // hitting us — both were needed for Refresh to actually re-fetch.
      // embed-tile-session: `resume` is a card's opaque request to be handed BACK its
      // existing state rather than have new state built. A tile refresh is the only way a
      // stateful card receives anything (a card push travels as MAIL on a chat stream, and a
      // tile has no chat thread) — so without this the refresh reports the change by
      // destroying it. The value is never interpreted here; it is echoed to the helper.
      const parts = [];
      if (force) parts.push('fresh=1');
      if (resume) parts.push(`resume=${encodeURIComponent(resume)}`);
      const q = parts.length ? `?${parts.join('&')}` : '';
      const res = await fetch(
        `/api/embed/s/${encodeURIComponent(getEmbedId() || '')}${q}`,
        { signal: ac.signal, cache: 'no-store' },
      );
      if (!res.ok) { setEmbedError(true); return; }
      const body = await res.json();
      // The action may emit several cards (e.g. JobSearch returns 5) — replace all.
      if (body && Array.isArray(body.cards)) {
        setEmbedError(false);
        // gallery-card-paging: the pager restarted at page 1, so bring the paired screen
        // back with it.
        //
        // Keyed on THIS COMPONENT'S FIRST LOAD, which is precisely what "the pager
        // restarted" means: `CardViewer`'s page is a fresh `useState` per mount. A Gallery
        // Refresh re-mounts the iframe, so it fires; a card action re-fetches IN PLACE on the
        // same mount and deliberately keeps its page (CardViewer.test.js: "an equal-size
        // refresh keeps the current page"), so it does not.
        //
        // NOT keyed on the `fresh=1` flag, which correlates with in-place today but means
        // "bypass the cache" — inferring mount-ness from a caching flag is the kind of proxy
        // that rots silently. The server can't distinguish these at all, which is why this
        // lives here. (!678 review.)
        if (!screenResetRef.current) {
          screenResetRef.current = true;
          const eid = getEmbedId();
          if (eid) {
            fetch(`/api/gallery/${encodeURIComponent(eid)}/page`, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ reset: true, cards: body.cards.length }),
            }).catch(() => { /* no screen, offline, or not ours */ });
          }
        }
        setEmbedPublic(!!body.public);   // card-gallery: branding only when public
        setEmbedStationLink(safeStationLink(body.station_link));
        // A card's Continue action targets this station when the card names none.
        if (Number.isInteger(body.continue_station)) setEmbedStation(body.continue_station);
        setEmbedBookmarked(typeof body.bookmarked === 'boolean' ? body.bookmarked : null);
        // reinvoke-embed-actions: an ACTABLE tile opens the WS action channel, once.
        // `actable` + `station_id` are server-resolved per caller (signed-in → their
        // own station access; anonymous → the station's embed_act opt-in) and the
        // station can only come from this response — the URL token is opaque. The
        // socket is the card's action channel only (the middleware's re-invoke
        // branch neither observes nor starts a thread). No actable → never connects,
        // read-only tiles behave exactly as before.
        // gallery-efficiency: open the action/station channel ONLY when one of the
        // embed's cards actually needs a live backend channel. A sandboxed 'display'
        // card renders from THIS response and never acts, so it opens no socket at
        // all — and with no channel the station never lands in Redux, so
        // SandboxedCard's qlink socket can't open either (0 sockets). embedCard-
        // NeedsChannel is FAIL-SAFE and inspects the whole card: build-time inline
        // cards, kind-less GenericCards with an action button (JobSearch's Apply),
        // and any unresolvable kind keep the channel — only a positively-identified
        // display card is dropped. See ADR 2026-08-13-gallery-efficiency.
        if (body.actable && body.station_id != null && !embedWsRef.current) {
          const needs = (await Promise.all(
            (body.cards || []).map((c) => embedCardNeedsChannel(c)),
          )).some(Boolean);
          if (needs && !embedWsRef.current) {
            embedWsRef.current = true;
            setEmbedStation(body.station_id);
            dispatch(connectWebSocket(wsUrl()));
          }
        }
        // Bump a generation so the viewer RE-MOUNTS each refreshed card (a
        // sandboxed card only ingests data via host:init at iframe load, so
        // reusing the instance would keep showing stale data — the "had to
        // reload the whole page" symptom). See TextViewer's cardKey.
        const gen = (embedGenRef.current += 1);
        dispatch(clearEvents());
        body.cards.forEach((c) => dispatch(addEvent(
          c && typeof c === 'object' ? { ...c, __embedGen: gen } : c)));
        // "Last run" = the card's SERVER-side generation time when present (the
        // true run instant — cache-aware, same for all viewers); else the fetch
        // time. `generated_epoch` is Unix SECONDS (guard ms too). Stored as ms;
        // rendered with toLocaleTimeString → each viewer's local timezone.
        // /ultrareview #6: use != null so a legitimate epoch of 0 isn't dropped.
        const ge = body.cards.map((c) => c && c.generated_epoch)
                             .find((v) => typeof v === 'number');
        setEmbedLastRun(
          typeof ge === 'number' ? (ge < 1e12 ? ge * 1000 : ge) : Date.now()
        );
      } else {
        setEmbedError(true);
      }
    } catch (e) {
      // A superseded/unmounted fetch aborts — not a user-facing error.
      if (e && e.name === 'AbortError') return;
      setEmbedError(true);
    } finally {
      // Only the latest request owns the spinner/ref (a superseded one leaves
      // them to its successor, still in flight).
      if (embedAbortRef.current === ac) {
        embedAbortRef.current = null;
        setEmbedRefreshing(false);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (isReinvokeEmbed()) {
      // re-invoke-on-open (the /embed/s/<token> shape): fetch the card(s) once;
      // the header refresh button re-runs the same action. No WS — a read-only
      // re-invoke embed neither observes nor drives a station. The interactive /
      // legacy numeric embed shape falls through to connectWebSocket (observe+act).
      refreshEmbed();
      // reinvoke-embed-actions P2: after a card fires an invoke (a move), re-fetch
      // this tile's render so the fresh, viewer-authoritative state replaces the
      // board. Debounced + delayed a beat so the helper commits first. The WS
      // invoke ack carries no data — this re-fetch IS the reply channel.
      let refreshTimer = null;
      const onInvoked = (e) => {
        const resume = (e && e.detail && e.detail.resume) || '';
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => refreshEmbed(true, resume), 1200);
      };
      window.addEventListener('aveq:card-invoked', onInvoked);
      return () => {
        window.removeEventListener('aveq:card-invoked', onInvoked);
        if (refreshTimer) clearTimeout(refreshTimer);
        if (embedAbortRef.current) embedAbortRef.current.abort();
        // reinvoke-embed-actions: tear down the action channel if one was opened.
        if (embedWsRef.current) { embedWsRef.current = false; dispatch(disconnectWebSocket()); }
      };
    }
    dispatch(connectWebSocket(wsUrl()));
    return () => dispatch(disconnectWebSocket());
  }, [dispatch, refreshEmbed]);

  useEffect(() => {
    const name = station?.station?.name;
    document.title = name ? `Ave Q: ${name}` : 'Ave Q';
  }, [station]);

  // Auto-open the output drawer whenever fresh card output arrives — even after
  // the user has closed it. A new turn REPLACES `events` (the eventsStale soft-
  // clear, then the first card resets the array) and appends grow it, so the
  // array REFERENCE changes on any new output. Opening on that (rather than on
  // length growth) means a same-size next turn (1 card → 1 card) still re-opens
  // the panel. markEventsStale alone doesn't swap the array, so it can't trigger
  // a premature open.
  const prevEventsRef = React.useRef(events);
  useEffect(() => {
    if (events !== prevEventsRef.current) {
      prevEventsRef.current = events;
      // collapse_panel: a card can ask the drawer to SLIDE OUT to the chat
      // instead of opening (tobin's view_dish re-emits the menu, which the
      // chat already links, but the dish itself is the reply to read now).
      // newestPanelCard parses the newest real card (entries may be objects or
      // JSON strings; plain-text progress can trail them) — see panelFocus.js.
      const lastCard = newestPanelCard(events);
      if (lastCard && lastCard.collapse_panel) {
        setOutputOpen(false);
      } else if (events && events.length > 0 && hasOutputPanel && !eventsRestored) {
        // Output restored on open stays closed: the chat's result links reach it.
        setOutputOpen(true);
      }
    }
  }, [events, eventsRestored, hasOutputPanel]);

  // A new or updated live event opens the panel onto it.
  const prevLiveOpenSeq = React.useRef(live.openSeq);
  useEffect(() => {
    if (live.openSeq !== prevLiveOpenSeq.current) {
      prevLiveOpenSeq.current = live.openSeq;
      if (hasOutputPanel) setOutputOpen(true);
    }
  }, [live.openSeq, hasOutputPanel]);
  const openLive = React.useCallback((id) => {
    dispatch(setActiveLive(id));
    setOutputOpen(true);
  }, [dispatch]);
  // Its card arrives as output, which opens the panel onto it.
  const fetchLive = React.useCallback((id) => {
    if (!station || !station.station) return;
    dispatch(sendLiveOpen(id, station.station.station_id, station.sessionId, threadId));
  }, [dispatch, station, threadId]);

  // card-reveal: a card can say "something is happening in here and you may not be able to
  // see it". The drawer above opens on a NEW `events` array, which covers a fresh card — but
  // a run that unfolds inside an existing card arrives by host:push and never touches
  // `events`, so the robot walked its board behind a closed drawer. The card asks; the
  // drawer opens. It cannot close it, and it cannot open one that does not exist.
  useEffect(() => {
    if (!hasOutputPanel) return undefined;
    const onReveal = () => setOutputOpen(true);
    window.addEventListener('aveq:card-reveal', onReveal);
    return () => window.removeEventListener('aveq:card-reveal', onReveal);
  }, [hasOutputPanel]);

  // A card action that asks the user something in chat slides the drawer out, so the question
  // and its progress are in view rather than behind the panel.
  useEffect(() => {
    const toChat = () => setOutputOpen(false);
    window.addEventListener(CARD_TO_CHAT, toChat);
    return () => window.removeEventListener(CARD_TO_CHAT, toChat);
  }, []);

  // output-panel-scroll-lock: while the output drawer is slid out on mobile (it covers
  // the chat), freeze the page so a touch / over-scroll on the drawer can't bleed through
  // and scroll the chat behind it. Restored on close. Desktop is left alone — there both
  // panels are visible side-by-side and should stay scrollable. (Shared with AuthHeader's
  // nav slide-out via useBodyScrollLock.)
  useBodyScrollLock(outputOpen && isMobile);

  // Reconnect on user tap, or on their return to the tab, when the connection is stale. The WS middleware's
  // SUBSCRIBER_METRICS branch handles resume-vs-fresh based on
  // pendingResumeThreadId (already stashed on the prior close).
  const reconnect = () => {
    dispatch(connectWebSocket(wsUrl()));
  };
  const reconnectingOnReturn = useReconnectOnReturn(isStale, reconnect, !outputOnly);

  // output-second-screen: a read-only view screen (e.g. a wall display) is
  // passive — nobody taps a reconnect overlay — so it AUTO-reconnects on a drop
  // (re-observing the thread on the fresh socket) instead of showing the overlay.
  // The chat view keeps the deliberate tap-to-reconnect (ws-reconnect slug).
  useEffect(() => {
    // embeddable-card-live-action: a re-invoke embed has no WS to reconnect (it's
    // a one-shot fetch), so exclude it from the passive-view auto-reconnect. The
    // interactive/legacy numeric embed DOES observe over a WS, so it stays in.
    if (!outputOnly || !isStale || isReinvokeEmbed()) return;
    const t = setTimeout(() => dispatch(connectWebSocket(wsUrl())), 3000);
    return () => clearTimeout(t);
  }, [outputOnly, isStale, dispatch]);

  // kiosk-send-split-link: a pinned-token view carrying `?fallback=<home>` is
  // an unattended kiosk screen. On denial (expired/invalid/audience-mismatch
  // token) navigate back to the home station's shared view rather than parking
  // on the denial message — the device supervisor can't see page state, so the
  // SPA (where the denial IS visible) owns the recovery. Short delay so the
  // reason is glimpsable on a monitored screen.
  useEffect(() => {
    if (!outputOnly || !(observeStatus && observeStatus.denied)) return;
    const fb = getFallbackStation();
    if (fb == null) return;
    const t = setTimeout(() => {
      window.location.replace(`/s/${fb}/view?shared=1`);
    }, 3000);
    return () => clearTimeout(t);
  }, [outputOnly, observeStatus]);

  // gallery-fit-content: a CONTENT-card embed (a non-fill viewer, e.g. `card`/`text`) reports its
  // rendered height to the parent (the Gallery, same-origin) so the tile sizes to the card instead
  // of a fixed box. Gated to `isEmbed()` + non-fill: a game/simulator embed keeps its fixed layout
  // and never beacons, so the Gallery leaves its default height. The content-fit layout below makes
  // documentElement's scrollHeight track the card's own content. No feedback loop: body height is
  // `auto` (content-driven), not derived from the iframe's box.
  useEffect(() => {
    if (!(isEmbed() && !FILL_VIEWERS.has(viewer))) return undefined;
    // Measure the CONTENT root (the content-fit App div), NOT document/documentElement — the html
    // element's scrollHeight is viewport-bound (≥ the iframe's own height), so measuring it would
    // report the tile's current height right back and the tile would never shrink.
    const post = () => {
      const el = embedRootRef.current;
      const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
      if (h > 0) {
        try { window.parent.postMessage({ type: EMBED_HEIGHT_MSG, height: h }, '*'); } catch (e) { /* cross-origin parent — ignore */ }
      }
    };
    let ro;
    if (window.ResizeObserver && embedRootRef.current) {
      ro = new ResizeObserver(post);
      ro.observe(embedRootRef.current);
    }
    post();
    const t = setTimeout(post, 300);   // re-post after fonts / the card iframe settle
    return () => { if (ro) ro.disconnect(); clearTimeout(t); };
  }, [viewer, events]);

  // gallery-touch-targets: post the card's last-run time to the parent (the Gallery) so it can show
  // it on the Refresh button's tooltip instead of a visible "Last run" line on the card. Harmless
  // when there's no parent frame (posts to self; no listener).
  useEffect(() => {
    if (!(isEmbed() && embedLastRun != null)) return;
    try { window.parent.postMessage({ type: EMBED_LASTRUN_MSG, lastRun: embedLastRun }, '*'); } catch (e) { /* cross-origin parent */ }
  }, [embedLastRun]);

  // station-chat-web-push: in a push-enabled shared room, ensure this logged-in
  // browser is subscribed for offline web-push. Idempotent (reuses an existing
  // PushSubscription) + best-effort (denied permission / unconfigured worker =
  // no-op). Off-room / push-off stations never touch push.
  //
  // webpush-client-gaps: this effect NEVER prompts. It only completes the
  // subscribe when permission is already 'granted' (returning users, and the
  // moment right after the opt-in below). Prompting from an effect has no user
  // gesture, which Safari refuses silently — so the ask lives in <PushOptIn/>,
  // rendered when permission is still 'default'.
  const webPushEnabled = isRoom && !!_cfg.web_push;
  const accountId = user && user.user_id;
  const stationId = station?.station?.station_id;
  const sessionId = station?.sessionId;
  const pushReady = webPushEnabled && isConnected && accountId && stationId && sessionId;
  const [pushPerm, setPushPerm] = useState(() => pushPermission());
  // "Not now" persists PER-STATION (webpush-client-gaps review): seed from
  // localStorage so a prior dismissal in THIS room survives a reload, and re-seed
  // when the station changes so a room the user never dismissed still offers it.
  const [pushDismissed, setPushDismissed] = useState(false);
  useEffect(() => {
    if (stationId) setPushDismissed(isPushOptInDismissed(stationId));
  }, [stationId]);
  const dismissNotice = useCallback(() => dispatch(setFloatingNotice(null)), [dispatch]);
  // A notice belongs to the station it came from.
  useEffect(() => () => { dispatch(setFloatingNotice(null)); }, [stationId, dispatch]);
  const dismissPush = useCallback(() => {
    dismissPushOptIn(stationId);
    setPushDismissed(true);
  }, [stationId]);

  const sendPushAction = useCallback(
    (uri, params) => dispatch(sendGlobalAction(uri, params, sessionId)),
    [dispatch, sessionId],
  );

  useEffect(() => {
    if (!pushReady) return;
    ensureSubscribed(stationId, accountId, sendPushAction);
  }, [pushReady, stationId, accountId, sendPushAction]);

  // The gesture path: prompt, then subscribe in the same click. Re-reads the
  // permission afterwards so the bar unmounts itself on grant (and stays gone on
  // deny — 'denied' is sticky and can only be undone in browser settings).
  const enablePush = useCallback(async () => {
    await ensureSubscribed(stationId, accountId, sendPushAction,
                           { requestPermission: true });
    setPushPerm(pushPermission());
  }, [stationId, accountId, sendPushAction]);

  const showPushOptIn = pushReady && !pushDismissed && pushPerm === 'default';

  // ---- station-chat-files -------------------------------------------------

  // attachments-multi-file: the upload loop is async and outlives renders, so it reads
  // the live chat log / prompting flag through refs rather than closed-over state.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const promptingRef = useRef(prompting);
  promptingRef.current = prompting;
  // chat-attach-with-message: files uploaded on a private station wait here until Send.
  const [staged, setStaged] = React.useState([]);
  // Log length just before the latest turn's local echo — `turnRepliedSince` against it
  // says whether that turn is still in flight.
  const lastAskRef = useRef(null);

  /** Post the uploaded file into the room as an ordinary message. The BFF
      builds the markdown (image → inline `![]()`, anything else → a sized
      link) so the link format has one definition, server-side. */
  const postFileMessage = useCallback((markdown) => {
    lastAskRef.current = messagesRef.current.length;
    dispatch(sendText("ask", markdown, station.station.station_id, station.sessionId, threadId));
    dispatch(addMessage({
      userId: user && user.user_id,
      displayName: user && user.display_name,
      message: markdown,
      isLocal: true,
    }));
  }, [dispatch, station, threadId, user]);


  /** Resolve once the assistant has replied to the file posted at log length `before`
      (or after `timeoutMs`, resolving false). Polling a ref, not subscribing — the loop
      is a one-off per paste and 250 ms granularity is plenty for a multi-second turn. */
  const waitForReply = useCallback((before, timeoutMs = 90000) => new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      if (turnRepliedSince(messagesRef.current, before)) return resolve(true);
      if (Date.now() - t0 > timeoutMs) return resolve(false);
      setTimeout(tick, 250);
    };
    tick();
  }), []);

  /** Upload one or more files. A room posts each at once; a private station stages them
      for Send (`sendStaged`).
      Sequential on purpose: a paste of several files should not open several
      concurrent multipart uploads against the same station's rate bucket.

      attachments-multi-file: on a PRIVATE station each post is an LLM turn, and the
      agent treats a message that lands while an expression is in flight as a
      CONTINUATION of it — so two file posts 4 s apart booked the first bill and
      silently dropped the second (dev, 2026-09-20). The next file therefore waits for
      the previous turn's reply. If that reply was a QUESTION (the model needs a
      vendor, say), the rest of the batch is held back with a notice rather than
      being consumed as the answer. Rooms keep the old fire-and-forget: a room post
      is not a turn. */
  const uploadAndPost = useCallback(async (files) => {
    const stationId = station?.station?.station_id;
    // Rooms, or a private station that opted in via `config.attachments`. In a
    // private station a plain message goes to the LLM (`_default_addressee` is
    // "llm"); that is only useful because the agent expands the posted link into
    // the file's text (aveq-agent/attachments.py) — without the opt-in the
    // assistant would just be handed a raw link, so the default stays off.
    if (!stationId || !canShareFiles || !files.length) return;
    setUploading((n) => n + files.length);
    const sequential = !isRoom;
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        try {
          dispatch(setTransientNotice(`Uploading ${file.name}…`));
          const info = await uploadFile(stationId, file);
          dispatch(setTransientNotice(null));
          if (sequential) {
            setStaged((prev) => [...prev, { name: file.name, size: file.size, markdown: info.markdown }]);
          } else {
            postFileMessage(info.markdown);
          }
        } catch (err) {
          // One bad file (too large, wrong permissions) must not abandon the
          // rest of a multi-file paste.
          dispatch(setTransientNotice(err.message || 'Upload failed.'));
        } finally {
          setUploading((n) => Math.max(0, n - 1));
        }
      }
    } catch (err) {
      setUploading(0);
      dispatch(setTransientNotice('Upload failed.'));
    }
  }, [dispatch, station, canShareFiles, isRoom, postFileMessage]);

  /** Paste and drop both deliver a DataTransfer — one handler serves both.
      Only intercept when the event actually carries files, so an ordinary
      text paste into the composer behaves exactly as before. */
  const handlePaste = useCallback((e) => {
    const files = filesFromDataTransfer(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    uploadAndPost(files);
  }, [uploadAndPost]);

  const handleDrop = useCallback((e) => {
    const files = filesFromDataTransfer(e.dataTransfer);
    setDragActive(false);
    if (!files.length) return;
    e.preventDefault();
    uploadAndPost(files);
  }, [uploadAndPost]);

  const openFilePicker = useCallback(() => {
    if (!canShareFiles) return;
    if (fileInputRef.current) fileInputRef.current.click();
  }, [canShareFiles]);

  const showFileList = useCallback(async () => {
    const stationId = station?.station?.station_id;
    if (!stationId || !canShareFiles) return;
    try {
      setFileList({ loading: true, files: [] });
      const files = await listFiles(stationId);
      setFileList({ loading: false, files });
    } catch (err) {
      setFileList(null);
      dispatch(setTransientNotice(err.message || 'Could not list files.'));
    }
  }, [station, canShareFiles, dispatch]);

  /* voice-input: the send path, taking the text as an ARGUMENT.
     submitMessage reads `inputMessage` from state, and React state updates are
     async — so the voice path can't "set the box then submit". Extracted rather
     than duplicated so both routes stay identical by construction. */
  const sendMessageText = useCallback((text) => {
    const t = text || '';
    if (!station || station.sessionId === '') {
      console.error("Session ID hasn't been set");
      return;
    }
    dispatch(setWaiting(true));
    // ephemeral-status-beats: clear any lingering transient status as a new turn starts.
    dispatch(setAssistantStatus(null));
    // Soft-clear: prior turn's events stay visible until the FIRST event of
    // this turn lands. Avoids the right panel going blank between submit
    // and the orchestrator's first event_callback (which can be ~seconds
    // for fan-out queries).
    dispatch(markEventsStale());
    // Fired here rather than in each caller: submitMessage, the Enter key and
    // the voice auto-send all funnel through this function, so they cannot drift.
    setSendFlash(true);
    if (sendFlashRef.current) clearTimeout(sendFlashRef.current);
    sendFlashRef.current = setTimeout(() => {
      sendFlashRef.current = null;
      setSendFlash(false);
    }, 220);
    if (t.trim() !== '') lastAskRef.current = messagesRef.current.length;
    dispatch(sendText("ask", t, station.station.station_id, station.sessionId, threadId));
    if (t.trim() !== '') {
      dispatch(addMessage({
        userId: user && user.user_id,
        displayName: user && user.display_name,
        message: t,
        isLocal: true
      }));
    }
  }, [dispatch, station, threadId, user]);

  // card-continue-at-station: a Continue button's opening line, sent once the new thread is bound.
  useEffect(() => {
    if (!threadId) return;
    // Prefilled, never sent: a link must not speak for the person.
    const say = takePendingSay();
    if (say) setInputMessage((cur) => cur || say);
  }, [threadId]);

  /** chat-attach-with-message: Send with staged files posts the text and the first file
      as ONE message. Each further file is its own turn, after the previous reply
      (attachments-multi-file). Nothing is posted while an earlier turn is still in flight:
      the agent would take it as a continuation and the reply is lost. */
  const sendStaged = useCallback(async (text, items) => {
    if (turnInFlight(messagesRef.current, lastAskRef.current)) {
      dispatch(setTransientNotice('Sending when the current reply arrives…'));
      await waitForReply(lastAskRef.current);
      dispatch(setTransientNotice(null));
    }
    sendMessageText(composeWithAttachment(text, items[0].markdown));
    for (let i = 1; i < items.length; i += 1) {
      dispatch(setTransientNotice(`Reading ${items[i - 1].name}… (${items.length - i} more waiting)`));
      await waitForReply(lastAskRef.current);
      dispatch(setTransientNotice(null));
      if (promptingRef.current) {
        const rest = items.slice(i).map((f) => f.name).join(', ');
        dispatch(setTransientNotice(`Answer the question first — not posted yet: ${rest}. Attach them again after.`));
        return;
      }
      postFileMessage(items[i].markdown);
    }
  }, [dispatch, sendMessageText, postFileMessage, waitForReply]);

  // ---- voice-input ---------------------------------------------------------

  const clearRestart = useCallback(() => {
    if (restartRef.current) {
      clearTimeout(restartRef.current);
      restartRef.current = null;
    }
  }, []);

  // `sendMessageText` is reached through a ref because the cancel window is
  // created ONCE and outlives every render that redefines it. Same for
  // `dispatch` — symmetrical, and correct even if its identity ever changes.
  const sendRef = useRef(sendMessageText);
  useEffect(() => { sendRef.current = sendMessageText; }, [sendMessageText]);
  const dispatchRef = useRef(dispatch);
  useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);

  // One notice per overflow EPISODE, not per dropped utterance — a burst is
  // many utterances and the point is to interrupt once, not to narrate. Cleared
  // when the queue drains, so the next burst speaks up again.
  const droppedRef = useRef(false);
  const cancelWindowRef = useRef(null);
  if (!cancelWindowRef.current) {
    cancelWindowRef.current = createCancelWindow({
      delayMs: SEND_DELAY_MS,
      send: (t) => sendRef.current(t),
      onChange: (pending, queued) => {
        setPendingVoice(pending);
        setQueuedCount(queued);
        if (queued === 0) droppedRef.current = false;
      },
      // The queue filled up, which in a mode people leave on usually means the
      // microphone is hearing a conversation rather than commands. Say so: the
      // `+N` badge is the honest display, but hands-free means nobody is
      // watching it, and this is the cue to switch the mode off.
      onDrop: () => {
        if (droppedRef.current) return;
        droppedRef.current = true;
        dispatchRef.current(setTransientNotice(
          'Too much at once — extra speech was ignored. Tap ⌨️ to turn voice off.'));
      },
    });
  }

  /** Abandon the transcript in its window — and anything queued behind it. */
  const cancelPendingVoice = useCallback(() => {
    cancelWindowRef.current.cancel();
  }, []);

  /** Send the transcript in its window now, then start the next one's window. */
  const flushPending = useCallback(() => {
    cancelWindowRef.current.flush();
  }, []);

  /** Open ONE recognition session. Re-armed by `onend` while the mode is on. */
  const listenOnce = useCallback(() => {
    const session = startVoice({
      onInterim: (t) => setHeard(t),
      onFinal: (t) => {
        const text = withMention(t, isRoom);
        if (!text) return;
        failuresRef.current = 0;          // a good utterance clears the streak
        setHeard('');
        // Queues behind anything already waiting rather than replacing it —
        // see voiceQueue.js for why replacing loses commands silently.
        cancelWindowRef.current.offer(text);
      },
      onError: (err) => {
        const msg = describeVoiceError(err);
        // A blocked mic can never succeed on retry — leave the mode rather than
        // restarting into the same refusal forever.
        if (err === 'not-allowed' || err === 'service-not-allowed') {
          blockedRef.current = true;
        } else if (err !== 'no-speech' && err !== 'aborted') {
          // `no-speech` is a PAUSE, not a failure — in a mode you leave on, the
          // user is expected to stop talking between commands.
          failuresRef.current += 1;
        }
        // `describeVoiceError` returns null for everything the user shouldn't be
        // told about, so there is nothing to filter here.
        if (msg) dispatch(setTransientNotice(msg));
      },
      onEnd: () => {
        voiceRef.current = null;
        setListening(false);
        setHeard('');
        // Safari ends a session after each utterance (and on silence). While the
        // mode is on that is not the end of listening — re-arm. Reading the REF
        // because this closure outlives the render that created it.
        if (!voiceModeRef.current) return;
        if (blockedRef.current || failuresRef.current >= MAX_VOICE_FAILURES) {
          // Something is persistently wrong (no mic, blocked, offline). Drop out
          // of the mode instead of spinning a restart loop the user can't see.
          voiceModeRef.current = false;
          setVoiceMode(false);
          dispatch(setTransientNotice('Voice mode turned off — recognition kept failing.'));
          return;
        }
        clearRestart();
        restartRef.current = setTimeout(() => {
          restartRef.current = null;
          if (voiceModeRef.current) listenOnce();
        }, VOICE_RESTART_MS);
      },
    });
    if (!session) {
      voiceModeRef.current = false;
      setVoiceMode(false);
      setListening(false);
      dispatch(setTransientNotice('Voice input is not available in this browser.'));
      return;
    }
    voiceRef.current = session;
    setListening(true);
  }, [isRoom, clearRestart, dispatch]);

  /** Turn voice mode on or off. On = the text box becomes the transcript. */
  const toggleVoiceMode = useCallback(() => {
    const next = !voiceModeRef.current;
    voiceModeRef.current = next;
    setVoiceMode(next);
    try { window.localStorage.setItem('aveq.voiceMode', next ? '1' : '0'); }
    catch (e) { /* private window / blocked site data — mode still works, just isn't remembered */ }

    if (next) {
      failuresRef.current = 0;
      blockedRef.current = false;
      listenOnce();
      return;
    }
    // Leaving the mode: stop everything, and drop a transcript still in its
    // window — turning voice off should not send one last thing afterwards.
    clearRestart();
    cancelWindowRef.current.cancel();
    setHeard('');
    if (voiceRef.current) {
      voiceRef.current.abort();
      voiceRef.current = null;
    }
    setListening(false);
  }, [listenOnce, clearRestart]);

  // Don't leave a recognition session, a restart, or a pending send running past
  // unmount.
  const cancelWindow = cancelWindowRef.current;
  useEffect(() => () => {
    cancelWindow.cancel();
    clearRestart();
    if (sendFlashRef.current) clearTimeout(sendFlashRef.current);
    voiceModeRef.current = false;
    if (voiceRef.current) voiceRef.current.abort();
  }, [cancelWindow, clearRestart]);

  // gallery-card-paging: a multi-card saved card (e.g. "Weather advice" — three pages) and
  // the Presto paired to its station page the SAME list independently, so before this they
  // drifted apart the moment anyone swiped: the tile moved, the screen did not.
  //
  // Sends a relative STEP, because that is the pager the device already has: `next` and
  // `previous` queue a step for its run loop, the same path the Control card's buttons and a
  // tap on the glass use. It keeps its own pager working, which narrowing the published frame
  // to a single card would have taken away.
  //
  // Called from the embed only (`pager` is passed on the fit-embed path, not in the chat
  // panel). A shared embed on someone else's page reaches the same endpoint and is refused
  // there — the route is owner-scoped — so the gate is the server's, not this flag's.
  //
  // Fire-and-forget: a screen that misses a step is one page out, and the next swipe still
  // works. Blocking the pager on a network round-trip would make the tile feel broken to
  // someone who has no Presto at all.
  const pageScreenWithCard = useCallback((delta) => {
    const id = getEmbedId();
    if (!id || !delta) return;
    fetch(`/api/gallery/${encodeURIComponent(id)}/page`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ delta: delta > 0 ? 1 : -1 }),
    }).catch(() => { /* no screen, offline, or not ours — the tile pages regardless */ });
  }, []);

  const submitMessage = () => {
    if (station.sessionId === '') {
        console.error("Session ID hasn't been set");
        return;
    }
    // voice-input: in voice mode there is no text box, so Send (and Enter) would
    // otherwise fall through the empty-input guard below and do NOTHING — a
    // button sitting there inert. Make it mean "send this now", skipping the
    // rest of the cancel window.
    if (voiceMode) {
      if (cancelWindowRef.current.pending) flushPending();
      return;
    }
    if (staged.length && !parseFileCommand(inputMessage)) {
      const items = staged;
      setStaged([]);
      setInputMessage('');
      sendStaged(inputMessage, items);
      return;
    }
    // Only allow empty input when there's an active prompt
    if (inputMessage.trim() === '' && !prompting) {
      return;
    }
    // station-chat-files slug: `@aveq upload file` / `@aveq download file` (and
    // the `/upload` `/files` slash forms) are UI affordances, not conversation
    // — open the picker or show the listing, and never send the line to the
    // room or the LLM. Intercepting here rather than resolving server-side
    // makes them deterministic; the LLM's function-selection is not.
    const fileCmd = parseFileCommand(inputMessage);
    if (fileCmd && canShareFiles) {
      setInputMessage('');
      if (fileCmd === 'upload') openFilePicker();
      else showFileList();
      return;
    }
    sendMessageText(inputMessage);
    setInputMessage('');
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    submitMessage();
  };

  const handleWidgetSubmit = (value) => {
    dispatch(setWaiting(true));
    dispatch(setWidget(null));
    dispatch(markEventsStale());
    dispatch(sendText("ask", value, station.station.station_id, station.sessionId, threadId));
    if (value.trim() !== '') {
      dispatch(addMessage({
        userId: user && user.user_id,
        displayName: user && user.display_name,
        message: value,
        isLocal: true
      }));
    }
  };

  const handleBoxClick = (opt) => {
    dispatch(sendText("ask", opt, station.station.station_id, station.sessionId, threadId));
    dispatch(addMessage({
      userId: user && user.user_id,
      displayName: user && user.display_name,
      message: opt
    }));
    dispatch(removeOptions());
  };

  // station-chat-load-older: the oldest server-id'd message currently shown
  // (room/replay messages carry a numeric msgId = SQLite row id). Backfill
  // pages before this. Only broadcasting rooms have such ids, so the "load
  // earlier" affordance shows only there. `> 1` hides it at the room's very
  // first message (cheap exhaustion check; the server also floors at the
  // user's join anchor).
  const roomMsgIds = messages
    .map((m) => Number(m.msgId))
    .filter((n) => Number.isFinite(n) && n > 0);
  const oldestShownId = roomMsgIds.length ? Math.min(...roomMsgIds) : null;
  const canLoadOlder = isConnected && oldestShownId !== null && oldestShownId > 1;

  const handleLoadOlder = () => {
    if (!canLoadOlder) return;
    dispatch(sendLoadOlder(oldestShownId, station.station.station_id, station.sessionId, threadId));
  };

  // desktop-side-rail: the rail replaces the horizontal header cluster on desktop
  // — one place to look, matching what mobile already does behind the hamburger.
  // Mobile keeps the hamburger + slide-out untouched.
  const showRail = !isMobile;
  const containerStyle = {
    width: '100%',
    // Mobile uses a DOCUMENT-scroll model (min-height so the page grows with the
    // message list) — scrolling the page collapses the iOS Safari toolbar, and
    // the header + input are position:sticky to stay pinned. Desktop keeps the
    // fixed 100dvh shell with the message list as an inner scroller. dvh (not
    // vh) tracks the visible viewport either way (100vh would overflow past the
    // iOS address bar).
    ...(isMobile ? { minHeight: '100dvh' } : { height: '100dvh' }),
    // desktop-side-rail: the permanent rail is position:fixed on the left, so it
    // is out of flow — the page just reserves its width. Done this way rather
    // than as a flex sibling because "permanent, full height, pinned to the
    // viewport" IS what fixed positioning describes, and because wrapping this
    // whole shell in a row would have meant restructuring the entire render for
    // a purely visual change. The mobile slide-out is fixed for the same reason.
    display: 'flex',
    flexDirection: 'column',
    // 20px around the panels on desktop reads as polish; on phone
    // viewports (~400px wide) it costs 10% of horizontal space — drop
    // to 8px on mobile so card content gets the breathing room instead.
    //
    // chat-header-seam: and only 3px at the BOTTOM on mobile. The input box is
    // `position: sticky; bottom: 0`, but sticky cannot leave its containing
    // block — which ends at this padding — so an 8px bottom padding pinned the
    // input 8px clear of the viewport and left a gap above Safari's toolbar.
    // The gap was this padding, not a margin on the input.
    //
    // 3px rather than 0: flush against the toolbar reads as a clipping bug, and
    // when the toolbar collapses on scroll the input follows the viewport down
    // to the screen edge, where a hairline of separation is what stops it
    // looking like it has fallen off.
    padding: isMobile ? '8px 8px 3px' : '20px',
    // desktop-side-rail: reserve the fixed rail's width. MUST come after the
    // `padding` shorthand above — a shorthand resets every side, so setting
    // paddingLeft before it is silently discarded (the header then renders
    // UNDER the translucent rail, which is exactly how this was found).
    ...(showRail ? { paddingLeft: `${RAIL_WIDTH + 20}px` } : null),
    boxSizing: 'border-box',
    background: '#fafbfc',
  };

  const topPanelStyle = {
    width: '100%',
    // Same logic for the header→panels gap. 20px is generous on a tall
    // desktop viewport; on a short phone screen it eats into the chat
    // panel's already-limited 60% height.
    //
    // chat-header-seam: on mobile this IS the gap above the chat card, and it
    // must match the gap below it — the input box's marginTop. Same constant,
    // so the card sits centred between the two rather than nearer one.
    marginBottom: isMobile ? CHAT_GAP : '20px',
    flexShrink: 0,
    display: 'flex',
    // Column so the optional station-info block flows below the
    // logo/icons row without moving them (the row keeps its own
    // alignItems: center).
    flexDirection: 'column',
    // station-header-surface: on desktop the header (avatar / name /
    // description) sits on a surface one step BRIGHTER than the page — #ffffff
    // against the shell's #fafbfc — so the cluster reads as a header rather than
    // as text floating on the background.
    //
    // It is a BAR, not a card, and that distinction is the whole point. Given
    // the same white + 1px #e0e0e0 border + 8px radius as the message card, it
    // read as a second chat surface — a rounded panel above a rounded panel, in
    // the same material, invites "is that another conversation?". A full-bleed
    // band with a single bottom rule and square corners is unambiguously chrome:
    // cards float, bars span.
    //
    // The negative margins cancel the shell's padding so the band reaches the
    // rail's edge and the viewport's right edge (`-20px` against containerStyle's
    // 20px; the left padding is RAIL_WIDTH + 20, so -20 stops exactly at the
    // rail rather than sliding under it). Same idiom as `.dir-topbar`'s
    // `margin: -32px -24px 28px` on the dashboard.
    //
    // Desktop only, and it must sit BEFORE the stickyHeaderStyle spread below:
    // on mobile that returns its own `background` (plus sticky + the bottom
    // rule), and the last writer wins. Mobile's header is already exactly this
    // shape — a page-coloured band with a rule — so it is left alone.
    ...(isMobile ? null : {
      background: '#ffffff',
      borderBottom: '1px solid #e0e0e0',
      margin: `-20px -20px 20px`,
      padding: '12px 20px',
      boxSizing: 'border-box',
      width: 'auto',
    }),
    // Mobile document-scroll: pin the header to the top of the visible viewport
    // as the page scrolls (which collapses the Safari toolbar). Opaque bg + a
    // high z-index so scrolled messages pass UNDER it, not through it.
    //
    // chat-header-seam: and a RULE along its bottom edge, matching the chat
    // card's own border colour. Passing under is the intent; looking open-topped
    // is not. The card's rounded top border slides beneath the bar the moment
    // you scroll, and without a rule the chat became an unbounded white area
    // running into a page-coloured band — the border read as clipped rather than
    // as covered. The bar's own edge now stands in for it, so the chat is
    // bounded at the top in every scroll position.
    ...stickyHeaderStyle(isMobile),
  };

  // On mobile (≤640px) the panels stack vertically — chat 60% on top,
  // output 40% below — each with its own scroll container. This is the
  // primary layout per shasta-notes/exploration/mobile-friendly-ui.md
  // §2; if iOS nested-scroll proves too irritating in real-device use,
  // Phase 2 of the slug switches mobile to a tab + floating-pill model.
  // Desktop layout (the existing 50/50 horizontal split) is unchanged.
  const panelsContainerStyle = {
    display: 'flex',
    width: '100%',
    // Desktop: fill the shell (flex:1) with an inner scroller (min-height:0 lets
    // it shrink below content). Mobile: grow with content (1 0 auto, never
    // shrink) so the PAGE scrolls instead — which collapses the Safari toolbar.
    ...(isMobile ? { flex: '1 0 auto' } : { flex: 1, minHeight: 0 }),
    flexDirection: isMobile ? 'column' : 'row',
    // Center the half-width chat panel horizontally on desktop.
    justifyContent: isMobile ? 'flex-start' : 'center',
  };

  // Chat panel: full width on mobile; a centred column capped at
  // CHAT_MAX_WIDTH on desktop. The output panel slides over it (drawerStyle).
  const leftPanelStyle = isMobile ? {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    // Grow with content (page scrolls) rather than fill-and-inner-scroll.
    flex: '1 0 auto',
  } : {
    // chat-panel-width: a CAP, not a fraction. This was `width: 50%` +
    // `flexShrink: 0`, which tied the panel to the window: at a 1727px viewport
    // it measured 744px with 372px of dead gutter on EACH side — as much empty
    // space as panel — and narrowing the window shrank the panel proportionally
    // while those gutters, the space that should have gone first, shrank too.
    //
    // `flex: 1` + `maxWidth` inverts that: the panel takes what it can up to the
    // cap, the gutters absorb whatever is left over, and narrowing spends the
    // gutters BEFORE touching the panel. Only once they reach zero does the
    // panel start giving up width.
    //
    // `minWidth: 0` is what lets it shrink past its content at that point —
    // without it a flex item refuses to go below min-content and the row
    // overflows instead.
    flex: 1,
    minWidth: 0,
    maxWidth: `${CHAT_MAX_WIDTH}px`,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  };

  // output-second-screen slug: the card OUTPUT panel as a right-side sliding
  // drawer. translateX(105%) parks it fully off-screen (incl. its shadow) when
  // closed; transform animates it in. Narrower on desktop than the old 50%
  // split since the chat no longer shares the row.
  const drawerStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100dvh',   // dvh (not vh) — match the visible viewport so the drawer's bottom controls stay on-screen on iOS
    // chat-ux-polish: wider output drawer (was min(560px, 50vw)) — the card panel
    // is the primary surface for output-heavy stations (job/restaurant results).
    // presto-control-card: full-width on mobile so it completely covers the chat panel
    // beneath (no peek) and edge-to-edge controls (e.g. the brightness slider) reach the screen.
    // chat-panel-width: the SAME constant the chat column is capped at, not a
    // matching literal. The two share this row, and a comment claiming they
    // share a number is worth nothing unless they actually share the symbol.
    width: isMobile ? '100vw' : `min(${CHAT_MAX_WIDTH}px, 66vw)`,
    maxWidth: '100%',
    background: '#ffffff',
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.18)',
    borderLeft: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    zIndex: 40,
    transform: outputOpen ? 'translateX(0)' : 'translateX(105%)',
    transition: 'transform 0.28s ease',
  };

  const rowStyle = {
    display: 'flex',
    flexDirection: 'row',
  };

  const logoAreaStyle = {
    display: 'flex',
    // desktop-side-rail: TOP-aligned, not centred. This is the box that actually
    // decides where the name sits — the name's column is its child (the avatar
    // was, until mobile-nav-left moved it to the trailing slot), and centring
    // means the name rides up as soon as the column grows a description
    // underneath it. (Fixing only the outer header row left
    // this one still centring, and the name still moved ~7px on every toggle.)
    // Top-aligned, the name's position is fixed and the description is purely
    // additive below it.
    alignItems: 'flex-start',
  };

  // mobile-nav-left: this box holds the hamburger, and it LEADS the mobile
  // header now (it used to trail it, flex:1 + text-align right, so the ☰ sat at
  // the right edge). It is only rendered on mobile — desktop's nav is the rail.
  // A fixed 40px box (the avatar's height) with the ☰ centred in it, so the
  // hamburger, the station name and the avatar share one centreline — the
  // name's button below is given the same 40px minHeight. Fixed heights keep
  // the row top-aligned (see the header comment) so opening the description
  // under the name still moves nothing else.
  const textBoxContainerStyle = {
    flexShrink: 0,
    marginRight: '8px',
    display: 'flex', alignItems: 'center', height: '40px',
  };

  const textBoxStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  };

  const boxStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '50px',
    height: '50px',
    border: '1px solid black',
    margin: '5px',
  };

  // output-second-screen slug: read-only second-screen mirror. The middleware
  // joined the owner's thread via observe_thread; its OUTPUT frames populate
  // `events` here, so we render ONLY the viewer/card panel — no chat scroll, no
  // input. A denied join (bad/expired token, or over the station's observer cap)
  // shows a short message instead.
  if (outputOnly) {
    // gallery-fit-content: a content-card embed lays out content-fit (height:auto) so it can report
    // its true height; games/simulator + the observe/split second screen keep the 100vh fill.
    const fitEmbed = isEmbed() && !FILL_VIEWERS.has(viewer);
    // gallery-share-ux: the Gallery loads its tiles chromeless (`?chrome=0`) — it supplies its own
    // Refresh/Share/Delete toolbar, so the embed's own header (aveq logo + refresh) is suppressed.
    // This also keeps the onaveq branding out of the owner's own board (a shared card shows the logo
    // to PUBLIC viewers of the standalone link, never on the gallery).
    let chromeless = false;
    try { chromeless = new URLSearchParams(window.location.search).get('chrome') === '0'; } catch (e) { /* no window.search */ }
    const denied = observeStatus && observeStatus.denied;
    const deniedReason = denied && observeStatus.reason;
    const deniedMsg =
      deniedReason === 'observer cap reached'
        ? 'This session already has the maximum number of output screens open.'
        : deniedReason === 'no active output'
        ? 'No active output yet — on the main screen, start a conversation and click “Split output”.'
        : deniedReason === 'not permissioned for this station'
        ? 'You’re not permissioned to view this station. Ask an operator for access, then re-open the link.'
        : 'This share link is invalid or expired — re-open it from the chat (“Split output”).';
    return (
      <div ref={embedRootRef} className="App" style={{ display: 'flex', flexDirection: 'column', height: fitEmbed ? 'auto' : '100vh', position: 'relative' }}>
        {/* No AuthHeader on a passive view window — it needs no profile,
            scheduler/notifications, or login chrome; just the output.
            embeddable-card-live-action: an EMBED gets a minimal branded header —
            aveq logo on the left; a refresh button (re-invoke) on the right for a
            re-invoke embed. Presto /view + observe stay chromeless. */}
        {isEmbed() && !chromeless && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0, padding: '8px 12px', borderBottom: '1px solid #e2e8f0',
          }}>
            {/* card-gallery: the onaveq branding shows only for a PUBLIC embed
                (shared / station-public). A private /gallery tile carries no logo;
                the empty span preserves space-between so refresh stays on the right. */}
            {embedPublic
              ? <img src="/onaveq.svg" alt="Ave Q" style={{ height: '28px', display: 'block' }} />
              : <span aria-hidden="true" />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GalleryBookmarkButton
              embedId={getEmbedId()} bookmarked={embedBookmarked} onChange={setEmbedBookmarked} />
            {isReinvokeEmbed() && (
              <button
                type="button" onClick={() => refreshEmbed(true)} disabled={embedRefreshing}
                title="Refresh" aria-label="Refresh"
                style={{
                  background: 'none', border: 'none', padding: 4,
                  cursor: embedRefreshing ? 'default' : 'pointer',
                  color: '#64748b', display: 'flex', alignItems: 'center',
                  opacity: embedRefreshing ? 0.5 : 1,
                }}>
                {/* single circular arrow (reload) */}
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
              </button>
            )}
            </div>
          </div>
        )}
        {denied ? (
          <div style={{ margin: 'auto', textAlign: 'center', color: '#64748b', padding: 24, maxWidth: 420 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 8 }}>Can’t open this output screen</div>
            <div style={{ fontSize: '0.9rem' }}>{deniedMsg}</div>
          </div>
        ) : (isReinvokeEmbed() && embedError) ? (
          // /ultrareview #6: surface a fetch/gate failure instead of a silent
          // blank box (403 not-public, 404 expired, network error, bad payload).
          <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8', padding: 24, maxWidth: 360 }}>
            <div style={{ fontSize: '0.95rem' }}>This embed is unavailable.</div>
          </div>
        ) : (
          <div style={fitEmbed
            ? { flex: 'none', position: 'relative' }
            : { flex: 1, minHeight: 0, position: 'relative', overflow: 'auto', overscrollBehavior: 'contain' }}>
            {/* output-idle-logo: same idle watermark as the Output drawer, for the
                SPLIT / observe second screen — a dedicated screen sitting blank is
                where "nothing here" reads most like "something is broken".

                NOT for embeds. This render path serves both, and an embed is a card
                on someone else's page: a faint Ave Q mark in an empty one is branding
                nobody asked for, which the `embedPublic` rule above is already careful
                about. `fitEmbed` also lays out height:auto, so a centred overlay has
                no box to centre in. Hence !isEmbed(). */}
            {!isEmbed() && showIdleWatermark(events) && (
              <div aria-hidden="true"
                   style={{ position: 'absolute', inset: 0, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            pointerEvents: 'none', userSelect: 'none' }}>
                <img src="/aveqlogo.svg" alt="" style={idleWatermarkImgStyle} />
              </div>
            )}
            {/* gallery-fit-content: a fit-embed renders through the chrome-free CardViewer
                (a re-invoke embed's `viewer` stays the default 'text' — no WS meta — so getViewer would
                give TextViewer's bordered/scrolling box). gallery-card-pager: multi-card embeds (e.g.
                JobSearch's 5) page through the cards via CardViewer's Previous/Next controls — one card
                at a time — instead of the old stacked-transcript fallback. */}
            {!isEmbed() && <LivePanel />}
            {fitEmbed
              ? <CardViewer events={events} pager onPageChange={pageScreenWithCard} />
              : !live.active && React.createElement(getViewer(viewer), { events })}
          </div>
        )}
        {isEmbed() && embedStationLink && (
          <div style={{ flexShrink: 0, padding: chromeless ? '6px 8px' : '10px 12px',
                        borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center',
                        gap: 8, flexWrap: 'wrap' }}>
            {/* Shown in Gallery tiles too. A new tab, because this page is often framed. */}
            {embedStationLink && (
              <a href={embedStationLink.url} target="_blank" rel="noopener noreferrer"
                 data-testid="embed-station-link"
                 style={{ background: '#0f172a', color: '#fff', borderRadius: 999,
                          padding: chromeless ? '5px 12px' : '8px 16px',
                          fontSize: chromeless ? '0.8rem' : '0.9rem', fontWeight: 600,
                          textDecoration: 'none' }}>
                Open {embedStationLink.name} on Ave Q
              </a>
            )}
          </div>
        )}
        {/* tile-card-actions: the card-invoke reply toast (a claim's
            celebration / a helper's refusal) — absolute overlay, auto-clears
            via the middleware's timer, never shifts the tile's flow height. */}
        {isReinvokeEmbed() && transientNotice && (
          <div role="status" style={{
            position: 'absolute', left: 10, right: 10, bottom: 10, zIndex: 5,
            padding: '10px 13px', background: '#1c1917', color: '#fafaf9',
            borderRadius: 10, fontSize: '0.8rem', lineHeight: 1.45,
            boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          }}>
            {transientNotice}
          </div>
        )}
        {/* No "tap to reconnect" overlay on a passive view screen — it
            auto-reconnects instead (see the outputOnly effect above). */}
        {/* gallery-touch-targets: hide the visible "Last run" line in the Gallery (chromeless) —
            the tile surfaces that time on the Refresh tooltip instead. Standalone embeds keep it. */}
        {isReinvokeEmbed() && embedLastRun != null && !chromeless && (
          <div style={{
            position: 'absolute', bottom: 3, right: 8, pointerEvents: 'none',
            fontSize: '0.62rem', color: '#cbd5e1', letterSpacing: '0.02em',
          }}>
            Last run {new Date(embedLastRun).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={containerStyle} onTouchStart={dismissKeyboardOnOutsideTouch}>
      {/* desktop-side-rail: fixed to the left edge, full height, always visible.
          Carries the same items the mobile sheet does — brand, nav links, Share,
          User, Log out — so there is one nav vocabulary across both. */}
      {showRail && (
        <AuthHeader variant="rail" links={stationNavLinks}
                    onStationSettings={openStationSettings} />
      )}
      {settingsOpen && (
        <StationSettingsModal stationId={station?.station?.station_id}
                              stationName={station?.station?.name}
                              onSaved={(body) => dispatch(setRoomAliases(aliasesFrom(body)))}
                              onClose={() => setSettingsOpen(false)} />
      )}
      {/* Top panel */}
      <div style={topPanelStyle}>
        {/* Header row — logo + station name + auth icons. Stays a flex
            row at its natural height; doesn't grow when the info block
            below opens. */}
        <div style={{
          display: 'flex', width: '100%',
          // desktop-side-rail: the station identity stays LEADING, unlike the
          // dashboard's centred AVE Q wordmark. Centring was tried and reverted:
          // the description lives in the name's column (so it can align to the
          // name), which makes that column much wider when the disclosure opens
          // — and a centred cluster then re-centres, shifting the name sideways
          // just from expanding it. Left-aligned, the column grows rightward and
          // the name does not move at all.
          //
          // ALWAYS flex-start, never conditional on the disclosure. Centring
          // while closed and topping while open made the name jump VERTICALLY on
          // every toggle — the column's height changes, so a centred name rides
          // it. Top-aligned to the avatar in both states, the name is fixed:
          // expanding only adds the description underneath it.
          alignItems: 'flex-start',
        }}>
          {/* mobile-nav-left: on mobile the header reads ☰ | station name | avatar
              — the hamburger leads, the station's picture trails at the right
              edge, and the sheet the ☰ opens slides out from the left, the side
              its trigger is on. desktop-side-rail: on desktop this cluster lives
              in the permanent left rail, so nothing renders here and the row is
              just name | avatar. */}
          {!showRail && (
            <div style={textBoxContainerStyle}>
              <AuthHeader links={stationNavLinks} showBrandLogo
                          onStationSettings={openStationSettings} />
            </div>
          )}
          {/* The station name's box. nav-brand-swap put the STATION's picture
              here, leading the header (this slot was the aveq logo, which moved
              to the nav's trailing slot on desktop and to the top of the sheet
              on mobile). mobile-nav-left then moved the picture to the TRAILING
              slot at the right edge — first on mobile, then on desktop too — so
              this box is the name alone now and grows to push the picture right.
              No wordmark here; the station's own identity is what belongs at
              the head of its page. */}
          <div style={{ ...logoAreaStyle, flex: 1, minWidth: 0 }}>
            {station?.station?.name && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <button
                  type="button"
                  onClick={() => setStationInfoOpen((v) => !v)}
                  aria-expanded={stationInfoOpen}
                  aria-label={stationInfoOpen ? 'Hide station info' : 'Show station info'}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px',
                    textTransform: 'uppercase', fontSize: 'inherit', color: 'inherit',
                    fontFamily: 'inherit',
                    // mobile-nav-left: same 40px line as the ☰ box (mobile) and
                    // the avatar (both), name centred within it. Only the button
                    // — the description below is unaffected.
                    minHeight: '40px',
                  }}>
                  <span>{station.station.name}</span>
                  <span style={{
                    fontSize: '0.7em', color: '#888', display: 'inline-block',
                    transform: stationInfoOpen ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}>▷</span>
                </button>
                {/* Station info block — the expandable disclosure, showing only
                    the description.

                    desktop-side-rail: it lives INSIDE the name's column now, so
                    it lines up with the station name by construction. It used to
                    sit below the whole header row with `paddingLeft: 39px`, a
                    constant measured against the logo's rendered width — which
                    only lines up while the header is left-aligned. Centring the
                    header makes the name's x depend on how long the name is, and
                    no constant can track that. */}
                {stationInfoOpen && station?.station?.description && (
                  <div style={{
                    marginTop: '4px', textAlign: 'left',
                    fontSize: '0.7rem', color: '#555', lineHeight: 1.4,
                  }}>
                    {station.station.description.length > 160
                      ? `${station.station.description.slice(0, 159)}…`
                      : station.station.description}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* mobile-nav-left: the station avatar trails the header on BOTH
              layouts, pinned to the right edge by the name box's flex:1.
              Top-aligned with the name like everything else in this row, so the
              info disclosure opening below the name doesn't move it. StationPic
              renders nothing when the station has no image_url. */}
          <div style={{ flexShrink: 0, marginLeft: '8px' }}>
            <StationPic size={40} />
          </div>
        </div>
        {/* nav-mobile-menu: confirm-before-leave dialog for the logo click. */}
      </div>
      {/* Container for left and right panels */}
      <div style={panelsContainerStyle}>
        {/* Left panel */}
        <div
          style={leftPanelStyle}
          onClick={() => { if (outputOpen) setOutputOpen(false); }}
        >
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', ...(isMobile ? { flex: '1 0 auto' } : { flex: 1, minHeight: 0 }) }}>
          <div ref={messagesContainerRef} style={{ ...(isMobile ? { flex: '1 0 auto' } : { flex: 1, overflowY: 'scroll' }), border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px', paddingBottom: isMobile ? '10px' : `${formHeight + 24}px`, background: '#ffffff' }}>
          {/* station-chat-load-older: backfill older room history (prepends). */}
          {canLoadOlder && (
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <button
                type="button"
                onClick={handleLoadOlder}
                style={{
                  background: 'none', border: 'none', color: '#2563eb',
                  fontSize: '0.8rem', cursor: 'pointer', padding: '4px 8px',
                }}
              >
                Load earlier messages
              </button>
            </div>
          )}
          {messages && messages.map((msg, index) => (
            msg.isSystem ? (
              <div key={index} style={{
                display: 'flex', alignItems: 'flex-start', marginBottom: '10px',
              }}>
                <div className="typing-indicator" style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic', padding: '6px 12px' }}>
                  {msg.message}
                </div>
              </div>
            ) : (
            <div key={index} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.isLocal ? 'flex-end' : 'flex-start',
              marginBottom: '10px',
            }}>
              {msg.isLocal ? (
                /* share-link-unfurl: text + preview together — see
                   ChatMessageBody on why they cannot be siblings. */
                <ChatMessageBody
                  text={msg.message}
                  bubbleStyle={{
                    maxWidth: '80%',
                    padding: '8px 12px',
                    borderRadius: '12px 12px 2px 12px',
                    background: '#dbeafe',
                    color: '#1a1a2e',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.9rem',
                  }}
                  onNavigate={(href) => { window.location.href = href; }}
                  onOpenShared={openShared}
                  onOpenImage={openImageInOutput}
                  compact={!isRoom}
                  openHint="Open in the output panel"
                />
              ) : msg.localCards ? (
                <a href="#result" data-testid="local-link" title="Open in the output panel"
                   onClick={(e) => { e.preventDefault(); openLocalInOutput(msg); }}
                   style={{ display: 'inline-block', marginTop: 4, fontSize: '0.85rem', fontWeight: 600,
                            color: '#2563eb', textDecoration: 'none' }}>
                  View result ›
                </a>
              ) : msg.scheduledRun ? (
                /* scheduled-result-view: a background job finished. A POINTER,
                   not the result — the run's page on /tasks holds the answer,
                   prose and cards alike. */
                <ScheduledRunLink
                  run={msg.scheduledRun}
                  taskName={msg.displayName}
                  label={msg.message}
                  onNavigate={(href) => { window.location.href = href; }}
                  onOpenShared={openShared}
                  compact={!isRoom}
                  openHint="Open in the output panel"
                />
              ) : msg.room ? (
                /* station-broadcast slug: another live visitor's message —
                   sender-tagged, plain bubble, no typing animation (it's
                   not the AI composing). */
                <div style={{ maxWidth: '80%' }}>
                  <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '2px', fontWeight: 600 }}>
                    {shownName(roomAliases, msg.displayName, msg.senderAccount)}
                  </div>
                  {liveLinkIn(msg.message) ? (
                    <LiveLink {...liveLinkIn(msg.message)} onOpen={openLive} onFetch={fetchLive} />
                  ) : (
                  <ChatMessageBody
                    text={msg.message}
                    bubbleStyle={{
                      padding: '8px 12px',
                      borderRadius: '12px 12px 12px 2px',
                      background: '#f1f5f9',
                      color: '#1a1a2e',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.9rem',
                    }}
                    onNavigate={(href) => { window.location.href = href; }}
                    onOpenShared={openShared}
                    onOpenImage={openImageInOutput}
                    compact={!isRoom}
                    openHint="Open in the output panel"
                  />
                  )}
                </div>
              ) : msg.message && liveLinkIn(msg.message) ? (
                <LiveLink {...liveLinkIn(msg.message)} onOpen={openLive} />
              ) : msg.message && shareLinkIn(msg.message) ? (
                /* autoroute-continuity-pin: an assistant message carrying a card link (the
                   turn's cards, or one the assistant reopened) unfurls like a shared one. */
                <ChatMessageBody
                  text={msg.message}
                  cardFallback
                  bubbleStyle={{ maxWidth: '80%', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                  onNavigate={(href) => { window.location.href = href; }}
                  onOpenShared={openShared}
                  compact={!isRoom}
                  openHint="Open in the output panel"
                />
              ) : (
                msg.message ?
                <div style={{ maxWidth: '80%', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                  <TypingEffect
                    text={msg.message}
                    delay={3}
                    key={index}
                    /* save-gallery-link: an internal link inside an assistant
                       message (e.g. the "save this" → /gallery reply) routes
                       through the same leave-station confirmation as the nav
                       links, since following it unloads the station page. */
                    onNavigate={(href) => { window.location.href = href; }}
                  />
                </div> : null
              )}
              {/* chat-helper-completion slug (#2): per-message timestamp on the
                  room + own-message bubbles. Relative label; full local
                  date-time on hover. AI typing-effect replies are left
                  untimestamped — they're the assistant composing, not a
                  room post. chat-helper-polish: gated on the per-station
                  `show_timestamps` opt-in. */}
              {showTimestamps && (msg.isLocal || msg.room) && msg.timestamp && (
                <div
                  title={absoluteTime(msg.timestamp)}
                  style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '2px' }}
                >
                  {relativeTime(msg.timestamp, nowTick)}
                </div>
              )}
              {msg.options && (
                <div style={{ ...rowStyle, marginTop: '6px' }}>
                  {msg.options.map((opt, i) => (
                    <div key={i} style={boxStyle} onClick={() => handleBoxClick(opt)}>
                      {opt}
                    </div>
                  ))}
                </div>
              )}
            </div>
            )))}
            {widget && !waiting && (
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                <InputWidget
                  widget={widget}
                  label={messages.length > 0 && !messages[messages.length - 1].isLocal
                    ? messages[messages.length - 1].message
                    : ''}
                  onSubmit={handleWidgetSubmit}
                />
              </div>
            )}
            {waiting && !showAnonPrompt && (
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div className="typing-indicator">
                  <span /><span /><span />
                </div>
              </div>
            )}
            {/* chat-helper-completion slug (#5): transient system notice
                (e.g. "Reconnected") — auto-clears, never persisted. */}
            {transientNotice && (
              <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  {transientNotice}
                </span>
              </div>
            )}
            {/* station-anon-login-prompt: replaces the `waiting` dots above, which
                for an anonymous visitor spin forever (no MAIL ever clears them). */}
            {showAnonPrompt && (
              <AnonLoginPrompt
                stationName={station?.station?.name}
                onSignIn={() => setLoginOpen(true)}
              />
            )}
            {connectionStatus === 'connecting' && !showAnonPrompt && (
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div className="typing-indicator" style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic', padding: '6px 12px' }}>
                  Connecting to Ave Q...
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {/* webpush-client-gaps: the opt-in for offline push. Sits at the
              bottom of the message panel, above the input — in the room it
              refers to, and only while permission is still 'default'. It exists
              because the permission prompt needs a USER GESTURE (Safari refuses
              one fired from a mount effect, silently). */}
          {showPushOptIn && (
            <PushOptIn onEnable={enablePush} onDismiss={dismissPush} />
          )}
          {/* chat-helper-polish slug: pin the "is typing…" line to the BOTTOM of
              the panel, just above the input — out of the scroll flow so it
              stays visible regardless of scroll position. Mobile: in-flow above
              the inline input. Desktop: absolute, anchored just above the
              floating input form (offset by its measured height). */}
          {/* ephemeral-status-beats: the assistant's transient "working…" status
              (e.g. "checking…"), pinned above the input like the typing line and
              cleared when the real reply lands. Shows in any session; never enters
              the chat scrollback (so it doesn't persist or replay). Stacked above
              the typing line when both are present. */}
          {assistantStatus && (
            <div style={isMobile ? {
              padding: '2px 6px 4px',
              flexShrink: 0,
            } : {
              position: 'absolute',
              left: 16,
              right: 16,
              bottom: formHeight + 10 + (isRoom && typingPeers && typingPeers.length ? 26 : 0),
              pointerEvents: 'none',
              zIndex: 5,
            }}>
              <span className="typing-indicator" style={{
                display: 'inline-block',
                fontSize: '0.8rem', color: '#777', fontStyle: 'italic',
                padding: '2px 10px',
                background: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.82)',
                borderRadius: '8px',
              }}>
                {assistantStatus}
              </span>
            </div>
          )}
          {isRoom && typingPeers && typingPeers.length > 0 && (
            <div style={isMobile ? {
              padding: '2px 6px 4px',
              flexShrink: 0,
            } : {
              position: 'absolute',
              left: 16,
              right: 16,
              bottom: formHeight + 10,
              pointerEvents: 'none',
              zIndex: 5,
            }}>
              <span className="typing-indicator" style={{
                display: 'inline-block',
                fontSize: '0.8rem', color: '#777', fontStyle: 'italic',
                padding: '2px 10px',
                background: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.82)',
                borderRadius: '8px',
              }}>
                {typingText(typingPeers.map((n) => shownName(roomAliases, n)))}
              </span>
            </div>
          )}
          <FloatingNotice text={floatingNotice} onDismiss={dismissNotice}
            floating={!isMobile} bottom={formHeight + 12} />
          {/* station-chat-files slug: the transient file listing `@aveq download
              file` opens. Deliberately NOT a message — a listing is personal,
              and posting one would spam the room. Dismisses on pick or close. */}
          {fileList && (
            <div style={{
              position: isMobile ? 'static' : 'absolute',
              left: 16, right: 16,
              bottom: isMobile ? undefined : formHeight + 12,
              zIndex: 6,
              maxHeight: '40%',
              overflowY: 'auto',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
              padding: '8px 10px',
              marginBottom: isMobile ? '6px' : 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', flexGrow: 1 }}>
                  Files in this station
                </span>
                <span
                  onClick={() => setFileList(null)}
                  style={{ cursor: 'pointer', fontSize: '0.9rem', color: '#94a3b8', padding: '0 4px' }}
                  role="button"
                  aria-label="Close file list"
                >×</span>
              </div>
              {fileList.loading && (
                <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Loading…</div>
              )}
              {!fileList.loading && fileList.files.length === 0 && (
                <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  Nothing shared yet — paste or drag a file into the chat to add one.
                </div>
              )}
              {!fileList.loading && fileList.files.map((f) => (
                <div key={f.sha256} style={{
                  display: 'flex', alignItems: 'baseline', gap: '8px',
                  padding: '3px 0', fontSize: '0.8rem',
                }}>
                  <a
                    href={f.url}
                    /* `download`: same-origin download WITHOUT a top-level
                       navigation, so the station's beforeunload guard never
                       fires. Same reasoning as MarkdownText's file links. */
                    download
                    style={{ color: '#2563eb', textDecoration: 'underline', flexGrow: 1, wordBreak: 'break-all' }}
                  >{f.name}</a>
                  <span style={{ color: '#94a3b8', fontSize: '0.72rem', flexShrink: 0 }}>
                    {humanSize(f.size)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <form
            ref={inputFormRef}
            onSubmit={handleChatSubmit}
            /* station-chat-files: the composer is the drop target. dragOver must
               preventDefault or the browser navigates to the dropped file. */
            onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            style={isMobile ? {
            // On mobile the form sits inline below the messages list as
            // its own row — no floating, no backdrop blur. The chat
            // panel is too short for the floating design to feel right
            // (input footprint competes with the messages instead of
            // overlaying them with room to spare). Mobile document-scroll: stick
            // the input to the bottom of the visible viewport as the page scrolls
            // (so it stays reachable while the toolbar collapses above).
            position: 'sticky',
            bottom: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'flex-end',
            background: '#ffffff',
            border: '1px solid #e0e0e0',
            borderRadius: '10px',
            padding: '6px 8px',
            // The gap BELOW the card. Same constant as the header's
            // marginBottom above it — see CHAT_GAP.
            marginTop: CHAT_GAP,
            flexShrink: 0,
          } : {
            position: 'absolute',
            left: 8,
            right: 8,
            bottom: 8,
            display: 'flex',
            alignItems: 'flex-end',
            background: 'rgba(255, 255, 255, 0.72)',
            borderRadius: '10px',
            padding: '6px 8px',
            boxShadow: inputFocused
              ? '0 8px 28px rgba(0, 0, 0, 0.35)'
              : '0 2px 12px rgba(0, 0, 0, 0.08)',
            transition: 'box-shadow 0.2s',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}>
            <CommandAutocomplete
              ref={commandAcRef}
              stationId={station?.station?.station_id}
              value={inputMessage}
              onPick={(t) => setInputMessage(t)}
            />
            {/* voice-input: speak instead of typing. Hidden entirely where the
                browser has no Web Speech API (Firefox) rather than offering a
                button that can't work. */}
            {voiceSupported() && (
              <div
                onClick={toggleVoiceMode}
                title={voiceMode ? 'Switch back to typing' : 'Switch to voice'}
                role="button"
                aria-label={voiceMode ? 'Switch back to typing' : 'Switch to voice'}
                aria-pressed={voiceMode}
                style={{
                  // Centred both ways: flex-end sat it on the composer's
                  // baseline, which reads as misaligned next to a two-row box.
                  alignSelf: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '34px',
                  height: '34px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  lineHeight: 1,
                  marginRight: '2px',
                  borderRadius: '8px',
                  color: '#64748b',
                  // Transparent in BOTH modes. The tint here used to say "voice
                  // is on", which the transcript panel replacing the whole text
                  // box already says far louder — so it was redundant chrome,
                  // and it read as the icon having a pink backplate rather than
                  // as a state. `aria-pressed` still carries the state to
                  // assistive tech, which is where it was actually needed.
                  background: 'transparent',
                }}
              >
                {/* The icon names what you switch TO, the way a keyboard's own
                    mic/keyboard toggle does — so in voice mode it offers the
                    keyboard back, rather than showing another microphone.
                    voice-icon-minimal: line icons (inline SVG, currentColor)
                    in place of the emoji — the emoji drew in the platform's
                    colour glyph set, heavy and off-palette against the
                    composer's muted chrome, and varied by OS. */}
                {voiceMode ? <KeyboardIcon /> : <MicIcon />}
              </div>
            )}
            {/* station-chat-files slug: the picker `@aveq upload file` opens.
                Hidden — paste and drag-drop are the primary gestures; this is
                the keyboard/command route to the same upload. */}
            {/* chat-attach-with-message: staged files, sent with the next message. Overlays
                the space above the composer so the composer keeps its height. */}
            {staged.length > 0 && (
              <div data-testid="staged-attachments" style={{
                position: 'absolute',
                left: 8,
                right: 8,
                bottom: 'calc(100% + 6px)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '6px',
                zIndex: 5,
              }}>
                {staged.map((f, i) => (
                  <span key={`${f.name}-${i}`} style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    fontSize: '0.8rem',
                    color: '#1e3a8a',
                  }}>
                    {f.name} · {humanSize(f.size)}
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setStaged((prev) => prev.filter((_, j) => j !== i))}
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: 0, lineHeight: 1 }}
                    >×</button>
                  </span>
                ))}
              </div>
            )}
            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                // Reset so re-picking the SAME file fires change again.
                e.target.value = '';
                if (picked.length) uploadAndPost(picked);
              }}
            />
            {/* station-chat-files slug: drop-target and in-flight feedback.
                Absolutely positioned so it overlays the composer rather than
                growing it — a composer that changes height mid-drag shifts the
                whole message list under the pointer. */}
            {(dragActive || uploading > 0) && (
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '10px',
                border: dragActive ? '2px dashed #2563eb' : '2px solid transparent',
                background: 'rgba(255,255,255,0.92)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.82rem',
                color: '#2563eb',
                pointerEvents: 'none',
                zIndex: 4,
              }}>
                {uploading > 0
                  ? `Uploading ${uploading} file${uploading === 1 ? '' : 's'}…`
                  : (isRoom ? 'Drop to share with the room' : 'Drop to attach')}
              </div>
            )}
            {voiceMode ? (
              /* voice-input: in voice MODE the transcript takes the text box's
                 place — same footprint, so the composer doesn't jump when the
                 mode flips. Live words while speaking, the finished command
                 during its cancel window, and a prompt when idle. */
              <div
                onClick={pendingVoice ? flushPending : undefined}
                style={{
                  flexGrow: 1,
                  marginRight: '10px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  background: pendingVoice ? '#eff6ff' : '#fafafa',
                  border: '1px solid ' + (pendingVoice ? '#2563eb' : 'transparent'),
                  fontSize: '0.9rem',
                  lineHeight: '1.4',
                  minHeight: '56px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: pendingVoice ? 'pointer' : 'default',
                  color: (pendingVoice || heard) ? '#1a1a2e' : '#94a3b8',
                }}
              >
                <span style={{ flexGrow: 1 }}>
                  {pendingVoice || heard
                    /* The idle line doubles as the signpost for what voice mode
                       COSTS: the textarea is gone, and with it paste-to-upload
                       and `/` completion. Silent unavailability in a mode people
                       are told to leave on is the confusing kind. */
                    || (listening
                      ? 'Listening… just speak · ⌨️ to type, paste files or use /commands'
                      : 'Starting microphone…')}
                </span>
                {queuedCount > 0 && (
                  /* Queued utterances are invisible otherwise — the point of
                     queueing was that a spoken command never disappears. */
                  <span
                    title={`${queuedCount} more waiting to send`}
                    style={{
                      flexShrink: 0,
                      fontSize: '0.75rem',
                      color: '#2563eb',
                      background: '#dbeafe',
                      borderRadius: '10px',
                      padding: '1px 8px',
                      whiteSpace: 'nowrap',
                    }}
                  >+{queuedCount}</span>
                )}
                {pendingVoice && (
                  <span
                    onClick={(e) => { e.stopPropagation(); cancelPendingVoice(); }}
                    role="button"
                    aria-label="Cancel"
                    title="Don't send this"
                    style={{ cursor: 'pointer', color: '#94a3b8', padding: '0 4px' }}
                  >✕</span>
                )}
              </div>
            ) : (
            <textarea
              ref={inputRef}
              /* !769 review #5: an anonymous visitor could type and press Send, and the
                 server silently DROPPED the frame -- bff/ws.py's `_anon_frame_allowed`
                 permits only SUBSCRIBE_STATION and the observe ACTIONs, so anything else
                 is discarded before it reaches qbric. Offering a composer that cannot
                 send is worse than the spinner was: it looks like it worked. While the
                 prompt is up, signing in is the only action on offer. */
              disabled={showAnonPrompt}
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                // chat-helper-completion slug (#3): emit a throttled typing
                // ping (room only, while connected, non-empty). The BFF fans
                // it to the room's other visitors; fire-and-forget.
                if (isRoom && isConnected && e.target.value.trim() !== '') {
                  const now = Date.now();
                  if (now - lastTypingSentRef.current > 3000) {
                    lastTypingSentRef.current = now;
                    dispatch(sendTyping(station.station.station_id));
                  }
                }
              }}
              onKeyDown={(e) => {
                // command-autocomplete: the dropdown handles ↑/↓/Enter/Esc first
                // and returns true when it consumed the key — so Enter picks a
                // suggestion instead of sending (only while the dropdown is up).
                if (commandAcRef.current && commandAcRef.current.handleKeyDown(e)) return;
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitMessage();
                }
              }}
              /* station-chat-files slug: a file copied in Finder (or any app)
                 pastes here as a real File. Only intercepted when the clipboard
                 actually carries files — a plain text paste is untouched. */
              onPaste={handlePaste}
              onFocus={() => setInputFocused(true)}
              onBlur={() => { setInputFocused(false); if (isMobile) snapInputToBottomAfterKeyboard(); }}
              placeholder={
                station?.station?.config?.resolver === "command"
                  ? "Command mode — try /list, /help, or /<set>.<fn> args"
                  : "Type a message... (Shift+Enter for newline)"
              }
              rows={2}
              style={{
                flexGrow: 1,
                marginRight: '10px',
                padding: '8px 14px',
                border: 'none',
                borderRadius: '8px',
                background: '#fafafa',
                outline: 'none',
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                lineHeight: '1.4',
                resize: 'none',
                minHeight: '56px',
                maxHeight: '120px',
                overflowY: 'auto',
              }}
            />
            )}
            <button type="submit" disabled={connectionStatus === 'connecting' || showAnonPrompt} style={{
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: connectionStatus === 'connecting' ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'center',
              opacity: connectionStatus === 'connecting' ? 0.4
                       : (sendFlash ? 0.55 : 1),
              // The press itself: a quick dip + shrink, then back. Transition is
              // asymmetric on purpose — instant down so it registers as a press,
              // eased back up so it reads as a button releasing.
              transform: sendFlash ? 'scale(0.82)' : 'scale(1)',
              transition: sendFlash ? 'none' : 'transform 0.18s ease-out, opacity 0.18s ease-out',
            }} title="Send">
              <SendGlyph />
            </button>
          </form>
          </div>
        </div>
      </div>

      {/* output-second-screen slug: the card OUTPUT panel as a right-side
          sliding drawer over the chat. */}
      <>
          {/* Mobile backdrop — tap outside to dismiss the drawer. */}
          {outputOpen && isMobile && (
            <div
              onClick={() => setOutputOpen(false)}
              // output-panel-scroll-lock: touchAction:none stops a touch-drag on the
              // backdrop from scrolling the chat list behind it (the backdrop only
              // captured taps before).
              style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.35)', zIndex: 39, touchAction: 'none' }}
            />
          )}
          <div style={drawerStyle}>
            {/* Drawer header: Ave Q logo + Split/Pin controls (left) + close (right).
                output-panel-chrome: logo at the panel's left edge; Split gated on
                output_observers_max >= 2, Pin gated on a linked display. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '8px', padding: '4px 10px', borderBottom: '1px solid #eef2f7', flexShrink: 0,
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                {/* output-panel-chrome: Ave Q logo, aligned to the panel's left edge,
                    before the "Split output" control. */}
                <img src="/onaveq.svg" alt="Ave Q" style={{ height: '24px', display: 'block', flexShrink: 0 }} />
                {hasOutputPanel && threadId && station?.station?.station_id ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {canSplitOutput && (splitToken && _stationUrlPath ? (
                      <a
                        href={`${_stationUrlPath}/${splitToken}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: '0.72rem', color: '#0084ff', textDecoration: 'none', fontWeight: 600 }}
                      >
                        Open output screen ↗
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => dispatch(sendSplit(station.station.station_id, station.sessionId, threadId))}
                        title="Open this conversation's card output on a second screen"
                        style={{ fontSize: '0.72rem', background: '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer', padding: '4px 10px', color: '#334155', fontWeight: 600 }}
                      >
                        Split output ⧉
                      </button>
                    ))}
                    {/* embeddable-card: when the station is publicly embeddable
                        (config.embed_public), offer a copyable <iframe> snippet so the
                        owner can paste the live card onto any page. Uses the current
                        origin so the snippet works on dev + prod. */}
                    {outputActions.embed && (
                      <button
                        type="button"
                        onClick={() => {
                          const snippet = `<iframe src="${window.location.origin}/embed/s/${station.station.station_id}" width="400" height="400" style="border:0" title="Ave Q card"></iframe>`;
                          if (navigator.clipboard && navigator.clipboard.writeText) {
                            navigator.clipboard.writeText(snippet).catch(() => window.prompt('Copy this embed snippet:', snippet));
                          } else {
                            window.prompt('Copy this embed snippet:', snippet);
                          }
                        }}
                        title="Copy an <iframe> snippet to embed this station's live card on any website"
                        style={{ fontSize: '0.72rem', background: '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer', padding: '4px 10px', color: '#334155', fontWeight: 600 }}
                      >
                        Embed ⧉
                      </button>
                    )}
                    {/* save-this-button: save the card on screen to the Gallery, labelled like
                        GalleryBookmarkButton since the two never show together. Sends the
                        chat verb rather than a bespoke endpoint — it reuses the whole tested
                        path (server-side embeddable re-check included), adds no new auth
                        surface, and leaves a visible record of what happened in the
                        transcript. Shown only when the card's package declares `save_as`, so
                        it never offers a save the fail-closed gate would refuse. */}
                    {outputActions.save && (
                      <button
                        type="button"
                        onClick={() => {
                          dispatch(sendText('ask', 'save this', station.station.station_id, station.sessionId, threadId));
                          dispatch(addMessage({
                            userId: user && user.user_id,
                            displayName: user && user.display_name,
                            message: 'save this',
                            isLocal: true,
                          }));
                        }}
                        title={savableCard.note
                          ? `Save this card to your gallery — ${savableCard.note}`
                          : 'Save this card to your gallery'}
                        aria-label={savableCard.note
                          ? `Add to gallery. ${savableCard.note}`
                          : 'Add to gallery'}
                        style={{ fontSize: '0.72rem', background: '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer', padding: '4px 10px', color: '#334155', fontWeight: 600 }}
                      >
                        Add to gallery ☆
                      </button>
                    )}
                    {/* presto-qr-pairing: pair a screen to THIS station from the room.
                        Gated on the station's own secondary-display opt-in — the same
                        flag the agent enforces server-side on redeem, so the button is
                        never offered for a pairing the backend would refuse. Sits
                        beside "Pin to display" deliberately: pair a screen, then pin to
                        it. The opt-in rule (including the legacy `presto_enabled`
                        alias) lives in `canPair` so it is stated once. */}
                    {canPair(_cfg) && (
                      <button
                        type="button"
                        onClick={() => setPairOpen(true)}
                        title="Add this station to a screen by scanning the code it shows"
                        style={{ fontSize: '0.72rem', background: '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer', padding: '4px 10px', color: '#334155', fontWeight: 600 }}
                      >
                        Pair ⧉
                      </button>
                    )}
                    {/* kiosk-send-split-link: pin THIS thread on the station's paired
                        display(s). output-panel-chrome: only shown when a display is
                        actually linked (station.has_paired_display, server-stamped). */}
                    {hasPairedDisplay && (
                      <button
                        type="button"
                        onClick={() => dispatch(sendSendToDisplay(threadId, station.sessionId))}
                        title="Show this conversation on the station's paired display"
                        style={{ fontSize: '0.72rem', background: '#f1f5f9', border: 'none', borderRadius: '12px', cursor: 'pointer', padding: '4px 10px', color: '#334155', fontWeight: 600 }}
                      >
                        Pin to display ⤴
                      </button>
                    )}
                  </span>
                ) : null}
              </span>
              <button
                type="button"
                onClick={() => setOutputOpen(false)}
                title="Back to chat"
                aria-label="Back to chat"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', fontWeight: 700, lineHeight: 1, color: '#64748b', padding: '2px 8px' }}
              >
                ›
              </button>
            </div>
            <LiveTabs items={live.items} active={live.active}
                      hasOutput={!showIdleWatermark(liveEvents) || !!sharedInOutput}
                      onSelect={(id) => dispatch(setActiveLive(id))} />
            <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'auto', overscrollBehavior: 'contain' }}>
              {/* output-idle-logo: with no events the viewer renders nothing, so an
                  opened-but-empty Output panel was a blank rectangle. Mirror what the
                  Presto does when it has no cards (presto-idle-logo,
                  aveq-agent/bff/view_image.py): a faint Ave Q watermark at 92% of the
                  SHORTER dimension. Opacity is 0.04 — HALF the Presto's 0.08, on
                  purpose: the Presto is a small reflective panel viewed across a room,
                  this is a bright backlit panel a foot from your face, and 0.08 read as
                  too present here. It is a whisper, not a
                  graphic, and it must never intercept a scroll or a click on the cards
                  that replace it, hence pointerEvents:none + aria-hidden. The parent is
                  already position:relative, so this centres over the whole body.
                  NOTE: the read-only second screen (`outputOnly`, the Split-output view)
                  has its own render path and is still blank when empty. */}
              <LivePanel open={outputOpen} />
              <div style={{ display: live.active ? 'none' : 'contents' }}>
              {showIdleWatermark(liveEvents) && !sharedInOutput && (
                <div aria-hidden="true"
                     style={{ position: 'absolute', inset: 0, display: 'flex',
                              alignItems: 'center', justifyContent: 'center',
                              pointerEvents: 'none', userSelect: 'none' }}>
                  <img src="/aveqlogo.svg" alt="" style={idleWatermarkImgStyle} />
                </div>
              )}
              {/* share-link-unfurl: a result opened from a chat link takes over
                  the panel. The live `events` are NOT touched — they keep
                  arriving underneath, so this is a state flip, not a refetch,
                  and a split second screen or a Presto watching the same
                  station never saw it.

                  run-result-unfurl REMOVED the "Back to live output" button
                  that used to sit in this header. It was written for the
                  original case — a link pasted from somewhere else, sitting
                  over your own live stream — and it does not survive the case
                  that now dominates: a scheduled run of THIS station, whose
                  result IS this station's output. Offering to go "back" to the
                  live output while showing it is a control that explains
                  nothing and undoes nothing worth undoing.

                  Nothing else clears `sharedInOutput`, so the panel holds the
                  result until another one is opened or the page reloads. That
                  is the intended behaviour, not an oversight: the result
                  staying put is what makes it readable. */}
              {sharedInOutput ? (
                <div data-testid="shared-in-output"
                     style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 10px', borderBottom: '1px solid #e2e8f0',
                                background: '#f8fafc' }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: '0.72rem', color: '#64748b',
                                   overflow: 'hidden', textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap' }}>
                      {sharedInOutput.title || 'Shared result'}
                    </span>
                    {sharedInOutput.asOf && (
                      <span data-testid="shared-as-of"
                            style={{ fontSize: '0.68rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                        {asOfLabel(sharedInOutput.asOf)}
                      </span>
                    )}
                    {sharedInOutput.refreshable && (
                      <button type="button" data-testid="shared-refresh"
                              disabled={!!sharedInOutput.refreshing}
                              onClick={() => refreshSharedInOutput(sharedInOutput.id)}
                              style={{ fontSize: '0.68rem', border: '1px solid #cbd5e1',
                                       borderRadius: 6, background: '#fff', padding: '1px 8px',
                                       cursor: 'pointer', color: '#334155' }}>
                        {sharedInOutput.refreshing ? 'Refreshing…' : '↻ Refresh'}
                      </button>
                    )}
                    {sharedInOutput.image && !sharedInOutput.image.gone && (
                      <a data-testid="shared-image-download" href={`${sharedInOutput.image.url}?download=1`}
                         download={sharedInOutput.image.name || true}
                         onClick={onImageDownload}
                         style={{ fontSize: '0.68rem', border: '1px solid #cbd5e1',
                                  borderRadius: 6, background: '#fff', padding: '1px 8px',
                                  color: '#334155', textDecoration: 'none' }}>
                        Download
                      </a>
                    )}
                    <GalleryBookmarkButton
                      embedId={sharedInOutput.id} bookmarked={sharedInOutput.bookmarked}
                      onChange={(v) => setSharedInOutput((cur) => (
                        cur && cur.id === sharedInOutput.id ? { ...cur, bookmarked: v } : cur))} />
                  </div>
                  <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                    {sharedInOutput.image ? (
                      sharedInOutput.image.gone ? (
                        <div data-testid="shared-image-gone"
                             style={{ padding: 16, fontSize: '0.8rem', color: '#94a3b8' }}>
                          {sharedInOutput.image.gone === 'gone'
                            ? 'This image is no longer available.'
                            : "This image couldn't be loaded."}
                        </div>
                      ) : (
                        <img data-testid="shared-image" src={sharedInOutput.image.url}
                             alt={sharedInOutput.image.name}
                             onError={() => {
                               const { url } = sharedInOutput.image;
                               imageFailure(url).then((why) => setSharedInOutput((cur) => (
                                 cur && cur.image && cur.image.url === url
                                   ? { ...cur, image: { ...cur.image, gone: why } } : cur)));
                             }}
                             style={{ display: 'block', maxWidth: '100%', maxHeight: '100%',
                                      margin: '0 auto', objectFit: 'contain' }} />
                      )
                    ) : sharedInOutput.loading ? null : (
                      sharedInOutput.cards.length
                        /* `pager` so a multi-card result pages here exactly as it
                           does on the shared page itself. */
                        ? <CardViewer events={sharedInOutput.cards} pager />
                        : <div style={{ padding: 16, fontSize: '0.8rem', color: '#94a3b8' }}>
                            This result is no longer available.
                          </div>
                    )}
                  </div>
                </div>
              ) : (
                React.createElement(getViewer(viewer), { events: liveEvents })
              )}
              </div>
            </div>
          </div>
      </>
      {/* envelope-key-resend: gated on 'stale' alone. It used to also require a
          prior live connection, but a first handshake that never completes (the
          ws handshake watchdog gives up → 'stale') then showed NOTHING — the page
          sat blank-but-dead. The initial 'connecting' state is still an inline
          hint, since 'connecting' !== 'stale'. */}
      {/* station-anon-login-prompt: belt-and-braces. The anonymous case observed in
          the browser sits at 'live', not 'stale', so this rarely fires -- but if the
          socket does drop, "Disconnected - reconnect" is the wrong story for a
          visitor who simply needs an account. */}
      {isStale && !showAnonPrompt && !reconnectingOnReturn && (
        <DisconnectedOverlay onReconnect={reconnect} />
      )}
      {/* The sign-in flow itself, so the prompt resolves the problem rather than
          only naming it. Mirrors AuthHeader's post-login WS bounce: the socket was
          opened anonymously, so it must be torn down and reopened to carry the new
          session. */}
      {loginOpen && (
        <LoginModal
          onClose={() => setLoginOpen(false)}
          onSuccess={() => {
            setLoginOpen(false);
            setAnonAuthed(true);
            reconnectWs(dispatch);   // shared with AuthHeader; see store/reconnectWs
          }}
        />
      )}
      {/* presto-qr-pairing: rendered OUTSIDE the output drawer so it isn't clipped by
          the drawer's overflow (and so closing the drawer can't strand it open). */}
      {pairOpen && station?.station?.station_id && (
        <PairDisplayModal
          stationId={station.station.station_id}
          stationName={station.station.name}
          onClose={() => setPairOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatInterface;